import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatCOP, formatNumber, API } from "@/lib/api";
import { cacheCatalog, getCachedCatalog, getCatalogCacheMeta } from "@/lib/offlineCache";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Package, Plus, Search, Edit2, Archive, Trash2, AlertTriangle, ArrowUpDown, ArrowRight, Upload, Download, FileSpreadsheet, Award, X, Check, ChevronRight, ChevronDown, Layers, Image as ImageIcon, Sparkles, Wand2, Boxes, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import UsageTip from "@/components/UsageTip";
import MoneyInput from "@/components/MoneyInput";
import CosteoCalculator from "@/components/CosteoCalculator";
import ContainerImportWizard from "@/components/ContainerImportWizard";
import ImportarLegacyBeemModal from "@/components/ImportarLegacyBeemModal";
import PDFExportButton from "@/components/PDFExportButton";
import { useNavigate } from "react-router-dom";
import ReordenHint from "@/components/ReordenHint";
import AccionesMenu from "@/components/AccionesMenu";
import ProductoImagenUploader from "@/components/ProductoImagenUploader";
import LotesInventarioSection from "@/components/LotesInventarioSection";
import RecostearContenedorDialog from "@/components/RecostearContenedorDialog";
import ConciliarContenedorDialog from "@/components/ConciliarContenedorDialog";
import FusionesDialog from "@/components/FusionesDialog";
import FiltroMenu from "@/components/FiltroMenu";
import DesglosarEmpaqueDialog from "@/components/DesglosarEmpaqueDialog";
import DesgloseResumenCard from "@/components/DesgloseResumenCard";
import DesgloseContenedorDialog from "@/components/DesgloseContenedorDialog";
import ImageLightbox from "@/components/ImageLightbox";
import FamilyDetailModal from "@/components/FamilyDetailModal";
import { ColorSwatch } from "@/lib/colors";
import { useHasPerm, useAuth } from "@/context/AuthContext";
import { useSuppressCritical } from "@/context/NotifSuppressContext";
import { fuzzyMatch } from "@/lib/fuzzySearch";

const UNIDADES = ["unidad", "metro", "rollo", "paquete", "docena", "yarda"];
const empty = { nombre: "", sku: "", codigo: "", descripcion: "", unidad: "unidad", costo: "", precio_sugerido: "", precio_minimo: "", categoria: "", stock_actual: 0, stock_minimo: 0, regla_comision_id: "default", activo: true, color: "", factor_empaque: "", motivo: "" };

const formDesde = (p) => ({
  nombre: p.nombre || "", sku: p.sku || "", codigo: p.codigo || "", descripcion: p.descripcion || "",
  unidad: p.unidad || "unidad",
  costo: p.costo ?? "",
  precio_sugerido: p.precio_sugerido || p.precio_base || 0,
  precio_minimo: p.precio_minimo || "",
  categoria: p.categoria || "",
  stock_actual: p.stock_actual || 0, stock_minimo: p.stock_minimo || 0,
  regla_comision_id: p.regla_comision_id || "default",
  activo: p.activo !== undefined ? p.activo : !p.archivado,
  color: p.color || "",
  factor_empaque: p.factor_empaque || "",
  motivo: "",
});

// Jun/2026: helpers del historial de contenedores
const pctFmt = (v) => (Number(v) <= 1 ? Math.round(Number(v) * 100) : Number(v));
function ContDato({ label, value, strong }) {
  const vacio = value === null || value === undefined || value === "" || value === "None";
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground/80">{label}</div>
      <div className={`truncate ${strong ? "font-semibold text-foreground" : "text-foreground/90"} ${vacio ? "text-muted-foreground/60" : ""}`}>{vacio ? "—" : String(value)}</div>
    </div>
  );
}

export default function ProductosPage() {
  const navigate = useNavigate();
  const canCreate = useHasPerm("productos.crear");
  const canEdit = useHasPerm("productos.editar");
  const canArchive = useHasPerm("productos.archivar");
  const canAdjustStock = useHasPerm("inventario.ajustar");
  const canEditStockMin = useHasPerm("productos.editar_stock_minimo");
  const canVerCosto = useHasPerm("productos.ver_costo");
  const [items, setItems] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [q, setQ] = useState("");
  const [bajoStock, setBajoStock] = useState(false);
  const [filtroStock, setFiltroStock] = useState("todos"); // todos | sin_stock | bajo_stock | con_stock
  // Feb/2026: filtro para encontrar productos huérfanos tras una importación
  // de contenedor (muchos quedan sin categoría o sin código real asignado).
  const [filtroProblema, setFiltroProblema] = useState("ninguno"); // ninguno | sin_categoria | sin_codigo | sin_sku
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [adjust, setAdjust] = useState({ cantidad: "", motivo: "" });
  const [containerWizardOpen, setContainerWizardOpen] = useState(false);
  const [legacyBeemOpen, setLegacyBeemOpen] = useState(false);
  // Agrupar variantes
  const [agruparOpen, setAgruparOpen] = useState(false);
  const [agruparLoading, setAgruparLoading] = useState(false);
  const [agruparGrupos, setAgruparGrupos] = useState([]);
  const [agruparSel, setAgruparSel] = useState({});
  const [agruparSaving, setAgruparSaving] = useState(false);
  // Modal "Crear nuevo grupo" — el director asigna un nombre al grupo y los
  // productos seleccionados quedan como variantes de un padre virtual (mar/2026).
  const [nuevoGrupoOpen, setNuevoGrupoOpen] = useState(false);
  const [nuevoGrupoNombre, setNuevoGrupoNombre] = useState("");
  const [nuevoGrupoBuscar, setNuevoGrupoBuscar] = useState("");
  const [nuevoGrupoItems, setNuevoGrupoItems] = useState(new Set()); // ids de productos a agrupar
  const [nuevoGrupoSaving, setNuevoGrupoSaving] = useState(false);
  // Lightbox para ver fotos en grande
  const [lightbox, setLightbox] = useState(null); // { src, alt } | null
  // Historial de contenedores (para eliminar imports errados)
  const [histContOpen, setHistContOpen] = useState(false);
  const [pdfSignal, setPdfSignal] = useState(0);
  const [histContenedores, setHistContenedores] = useState([]);
  const [histContAbierto, setHistContAbierto] = useState(null); // Jun/2026: detalle expandible
  const [histContLoading, setHistContLoading] = useState(false);
  const [histContEliminando, setHistContEliminando] = useState(null); // id del contenedor en eliminación
  const [recostearCont, setRecostearCont] = useState(null); // Jun/2026: corregir TRM/% gastos de un contenedor
  const [bulkDesglosarOpen, setBulkDesglosarOpen] = useState(false); // Jun/2026: desglose de empaque masivo
  const [desgloseCont, setDesgloseCont] = useState(null); // Jun/2026: desglose de empaques por contenedor
  const [conciliarCont, setConciliarCont] = useState(null); // Jun/2026: conciliar lotes vs Excel
  const [fusionesCont, setFusionesCont] = useState(null); // Jun/2026: deshacer fusiones del importador ({} = todas)
  const cargarHistContenedores = async () => {
    setHistContLoading(true);
    try {
      const { data } = await api.get("/productos/_/contenedores?limit=100");
      setHistContenedores(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el historial");
    } finally { setHistContLoading(false); }
  };
  const eliminarContenedor = async (cont) => {
    const motivo = window.prompt(
      `⚠ ATENCIÓN: vas a eliminar el contenedor "${cont.referencia}".\n\n` +
      `Se revertirá el stock que agregó (${(cont.unidades_recibidas || 0).toLocaleString("es-CO")} und.), ` +
      `y los productos creados exclusivamente por este contenedor serán archivados.\n\n` +
      `Escribe el motivo (mínimo 5 caracteres):`
    );
    if (motivo === null) return;
    if (motivo.trim().length < 5) {
      toast.error("El motivo es obligatorio (mínimo 5 caracteres)");
      return;
    }
    setHistContEliminando(cont.id);
    try {
      const { data } = await api.post(`/productos/_/contenedores/${cont.id}/eliminar`, { motivo: motivo.trim() });
      toast.success(
        `Contenedor eliminado · ${(data.stock_revertido || 0).toLocaleString("es-CO")} und. revertidas · ${data.productos_archivados || 0} SKU archivado(s)`
      );
      await cargarHistContenedores();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally { setHistContEliminando(null); }
  };
  useEffect(() => {
    if (histContOpen) cargarHistContenedores();
    // eslint-disable-next-line
  }, [histContOpen]);
  // Jerarquía de variantes (2 niveles: padre → sub-padre → variante)
  const [jerarquiaOpen, setJerarquiaOpen] = useState(false);
  const [jerarquiaLoading, setJerarquiaLoading] = useState(false);
  const [jerarquiaGrupos, setJerarquiaGrupos] = useState([]); // {sugerido_padre_nombre, subgrupos:[{spec, sugerido_nombre_sub_padre, variantes:[{id, nombre_limpio, nombre_original, sku, color}]}], variantes_directas}
  const [jerarquiaSaving, setJerarquiaSaving] = useState(false);

  useEffect(() => {
    if (!jerarquiaOpen) return;
    let cancelled = false;
    (async () => {
      setJerarquiaLoading(true);
      setJerarquiaGrupos([]);
      try {
        const { data } = await api.get("/productos/_/detectar-jerarquia");
        if (!cancelled) setJerarquiaGrupos(data.grupos || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "No se pudo detectar jerarquías");
      } finally {
        if (!cancelled) setJerarquiaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jerarquiaOpen]);

  // Cargar sugerencias cada vez que se abre el modal
  useEffect(() => {
    if (!agruparOpen) return;
    let cancelled = false;
    (async () => {
      setAgruparLoading(true);
      setAgruparGrupos([]); setAgruparSel({});
      try {
        const { data } = await api.get("/productos/_/detectar-variantes", { params: { min_grupo: 2 } });
        if (cancelled) return;
        const grupos = Array.isArray(data) ? data : [];
        setAgruparGrupos(grupos);
        const initSel = {};
        grupos.forEach((g, idx) => {
          const primero = g.productos[0];
          initSel[idx] = {
            padre_id: primero?.id || "",
            ids: new Set(g.productos.slice(1).map((p) => p.id)),
          };
        });
        setAgruparSel(initSel);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "No se pudieron cargar sugerencias");
      } finally {
        if (!cancelled) setAgruparLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agruparOpen]);
  const [costeoDefaults, setCosteoDefaults] = useState({ trm: 4200, pct_gastos: 25, pct_margen_minimo: 30, pct_margen_sugerido: 60 });

  // Selección múltiple + asignación bulk de regla de comisión
  const [selected, setSelected] = useState(new Set());
  const [bulkRegla, setBulkRegla] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);
  // Editor masivo de precios (Feb/2026)
  const [bulkPreciosOpen, setBulkPreciosOpen] = useState(false);
  const [bulkPrecios, setBulkPrecios] = useState({
    modo: "fijo", // fijo | absoluto | porcentual
    aplicar_sugerido: true,
    aplicar_minimo: true,
    precio_sugerido_valor: "",
    precio_minimo_valor: "",
    porcentaje: "",
    motivo: "",
  });
  const [bulkPreciosSaving, setBulkPreciosSaving] = useState(false);
  // Editor masivo de código real (Feb/2026)
  const [bulkCodigoOpen, setBulkCodigoOpen] = useState(false);
  const [bulkCodigoValor, setBulkCodigoValor] = useState("");
  const [bulkCodigoSaving, setBulkCodigoSaving] = useState(false);
  // Editor masivo de unidad de empaque (Feb/2026)
  const [bulkEmpaqueOpen, setBulkEmpaqueOpen] = useState(false);
  const [bulkEmpaqueValor, setBulkEmpaqueValor] = useState("unidad");
  const [bulkEmpaqueSaving, setBulkEmpaqueSaving] = useState(false);
  // Modal de detalle de familia (todos los colores en grid con foto/stock/precio)
  const [familiaDetalle, setFamiliaDetalle] = useState(null); // { familiaKey, familiaNombre, variantes }
  // Oct/2026: cuando el usuario abre un edit/ajuste desde el modal familia,
  // al cerrar debe REGRESAR al modal familia CON DATOS FRESCOS (no stale).
  // Guardamos el snapshot completo como fallback + la key para reconstruir.
  const [familiaRetornoSnap, setFamiliaRetornoSnap] = useState(null); // snapshot original (fallback)
  // Ref a items para que el watcher siempre lea el estado más reciente,
  // sin depender del closure obsoleto cuando el PATCH + load() terminan.
  const itemsRef = useRef([]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // Reconstruye la familia desde el `items` actualizado (fresh data garantizado).
  // Si no encuentra nada (familia renombrada / vacía), devuelve el snapshot
  // original para no dejar al usuario varado en la lista general.
  const rebuildFamilia = useCallback((snap) => {
    if (!snap) return null;
    const list = itemsRef.current || [];
    const variantes = list.filter((it) => (it.familia_key || "") === snap.familiaKey);
    if (variantes.length === 0) {
      // Fallback: regresamos el snapshot original para que el modal reabra
      // igual (datos stale pero mejor que botar al usuario a la lista).
      return snap;
    }
    const famName = variantes[0].familia_nombre || variantes[0].nombre?.split(" - ")[0] || snap.familiaNombre || snap.familiaKey;
    return { familiaKey: snap.familiaKey, familiaNombre: famName, variantes };
  }, []);

  // Watcher: cuando el edit/adjust modal se cierra Y hay familia pendiente,
  // la restaura con datos FRESCOS (o fallback al snapshot original).
  useEffect(() => {
    if (!open && !adjustTarget && familiaRetornoSnap) {
      const snap = familiaRetornoSnap;
      setFamiliaRetornoSnap(null);
      // Delay corto para no colisionar con la animación de cierre del edit
      const t = setTimeout(() => {
        const fresh = rebuildFamilia(snap);
        if (fresh) setFamiliaDetalle(fresh);
      }, 120);
      return () => clearTimeout(t);
    }
  }, [open, adjustTarget, familiaRetornoSnap, rebuildFamilia]);
  // Modal de confirmación para eliminar toda una familia
  const [eliminarFamiliaTarget, setEliminarFamiliaTarget] = useState(null); // { familiaKey, familiaNombre, count }
  const [eliminarFamiliaMotivo, setEliminarFamiliaMotivo] = useState("");
  const [eliminandoFamilia, setEliminandoFamilia] = useState(false);

  // Suprimir modal crítico mientras se crea/edita producto, ajusta stock o se corre wizard de contenedor
  useSuppressCritical(open || !!adjustTarget || containerWizardOpen || legacyBeemOpen || !!familiaDetalle || !!eliminarFamiliaTarget || bulkPreciosOpen || bulkCodigoOpen || bulkEmpaqueOpen);

  // Estado de expansión de variantes (por producto padre)
  const [expanded, setExpanded] = useState({}); // { [padreId]: {loading, variantes:[], open} }
  // Nuevo: agrupar visualmente por familia_key en la tabla del director
  const [agruparEstilos, setAgruparEstilos] = useState(true);
  const [familiaAbierta, setFamiliaAbierta] = useState({}); // { [familia_key]: bool }

  const toggleVariantes = async (padre) => {
    const cur = expanded[padre.id];
    if (cur?.open) { setExpanded({ ...expanded, [padre.id]: { ...cur, open: false } }); return; }
    if (cur?.variantes) { setExpanded({ ...expanded, [padre.id]: { ...cur, open: true } }); return; }
    setExpanded({ ...expanded, [padre.id]: { loading: true, variantes: [], open: true } });
    try {
      const { data } = await api.get(`/productos/${padre.id}/variantes`);
      setExpanded((prev) => ({ ...prev, [padre.id]: { loading: false, variantes: data.variantes || [], open: true } }));
    } catch { setExpanded((prev) => ({ ...prev, [padre.id]: { loading: false, variantes: [], open: false } })); }
  };
  const { user } = useAuth();
  const isDirector = user?.role === "admin";
  // Feb/2026: la barra masiva y los checkboxes de selección se habilitan
  // para cualquiera con permiso `productos.editar` (admin + operador). Las
  // acciones destructivas (mover/fusionar familia) siguen gated a `isDirector`.
  const canBulk = isDirector || canEdit;

  useEffect(() => {
    api.get("/productos/_/costeo-defaults").then(({ data }) => setCosteoDefaults(data)).catch(() => {});
  }, []);

  const [offlineMeta, setOfflineMeta] = useState(null);
  const load = useCallback(async () => {
    // Cargamos SIEMPRE el catálogo completo (sin filtro de texto). El filtrado
    // por búsqueda se hace client-side para que sea instantáneo. Los toggles
    // de stock también se aplican en memoria.
    const params = new URLSearchParams();
    if (bajoStock) params.set("bajo_stock", "true");
    params.set("limit", "5000");
    try {
      const [pr, rr] = await Promise.all([
        api.get(`/productos?${params.toString()}`),
        api.get("/reglas-comision"),
      ]);
      setItems(pr.data);
      setReglas(rr.data);
      setOfflineMeta(null);
      if (!bajoStock) cacheCatalog("productos", pr.data);
    } catch (e) {
      const cached = getCachedCatalog("productos");
      if (cached && cached.length > 0) {
        setItems(cached);
        setOfflineMeta(getCatalogCacheMeta("productos"));
      } else {
        throw e;
      }
    }
  }, [bajoStock]);
  useEffect(() => { load(); }, [load]);

  // Recargar cuando el wizard de contenedor termine de subir fotos
  // (evento `beem:productos-cache-invalidated` disparado desde ContainerImportWizard)
  useEffect(() => {
    const handler = () => { load(); };
    window.addEventListener("beem:productos-cache-invalidated", handler);
    return () => window.removeEventListener("beem:productos-cache-invalidated", handler);
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      // Feb/2026: si el director cambió el "color/variante" y el nuevo valor
      // choca con otra variante del mismo grupo, ofrecemos fusionar stock
      // (mismo flujo que el modal familiar). Aborta el guardado si el usuario
      // prefiere renombrar en vez de fusionar.
      const nuevoColor = (form.color || "").trim();
      const colorPrevio = (editing?.color || "").trim();
      if (editing && nuevoColor && nuevoColor.toLowerCase() !== colorPrevio.toLowerCase()) {
        const famKey = (editing.familia_key || "").trim();
        if (famKey) {
          const hermanoDuplicado = items.find((otro) => {
            if (otro.id === editing.id) return false;
            const otroFam = (otro.familia_key || "").trim();
            if (otroFam !== famKey) return false;
            const otroColor = (otro.color || "").trim();
            return otroColor.toLowerCase() === nuevoColor.toLowerCase();
          });
          if (hermanoDuplicado) {
            const stockActual = Number(editing.stock_actual || 0);
            const stockDest = Number(hermanoDuplicado.stock_actual || 0);
            const msg =
              `Ya existe una variante "${nuevoColor}" en este grupo.\n\n` +
              `• Esta variante: ${stockActual} unidades\n` +
              `• Existente: ${stockDest} unidades\n` +
              `• Total al fusionar: ${stockActual + stockDest} unidades (2 variantes en 1)\n\n` +
              `Aceptar → fusionar el stock con la existente y archivar esta.\n` +
              `Cancelar → volver al formulario para cambiarle el nombre.`;
            if (!window.confirm(msg)) {
              return;
            }
            try {
              await api.post("/productos/variantes/merge", {
                source_id: editing.id,
                target_id: hermanoDuplicado.id,
              });
              toast.success(`Stock fusionado con "${nuevoColor}" existente`);
              const snap2 = familiaRetornoSnap;
              setOpen(false); setForm(empty); setEditing(null);
              await load();
              if (snap2) {
                setFamiliaRetornoSnap(null);
                setTimeout(() => {
                  const fresh = rebuildFamilia(snap2);
                  if (fresh) setFamiliaDetalle(fresh);
                }, 150);
              }
              return;
            } catch (err) {
              toast.error(err?.response?.data?.detail || "No se pudo fusionar");
              return;
            }
          }
        }
      }

      const payload = {
        nombre: form.nombre, sku: form.sku, codigo: form.codigo || null, descripcion: form.descripcion,
        unidad: form.unidad, categoria: form.categoria,
        costo: form.costo === "" ? null : Number(form.costo),
        precio_sugerido: Number(form.precio_sugerido) || 0,
        precio_minimo: form.precio_minimo === "" ? null : Number(form.precio_minimo),
        stock_actual: Number(form.stock_actual) || 0,
        stock_minimo: Number(form.stock_minimo) || 0,
        regla_comision_id: form.regla_comision_id || "default",
        activo: !!form.activo,
        color: nuevoColor,
      };
      if (editing) {
        payload.motivo = form.motivo || "Actualizacion manual";
        if (isDirector) payload.factor_empaque = form.factor_empaque === "" || Number(form.factor_empaque) <= 1 ? null : Number(form.factor_empaque);
        await api.patch(`/productos/${editing.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await api.post("/productos", payload);
        toast.success("Producto creado");
      }
      // Oct/2026: await load() ANTES de cerrar el modal — así cuando el
      // watcher reabra la familia tendrá los items frescos (fix "los cambios
      // no se aplican a la primera").
      await load();
      // Oct/2026 (segunda ronda): además del watcher, hacemos reopen EXPLÍCITO
      // aquí dentro para evitar race conditions con el ciclo de useEffect.
      // El watcher sirve como respaldo si el cierre viene por otra vía
      // (Esc / click fuera / botón Cancelar).
      const snap = familiaRetornoSnap;
      setOpen(false); setForm(empty); setEditing(null);
      if (snap) {
        setFamiliaRetornoSnap(null);
        setTimeout(() => {
          const fresh = rebuildFamilia(snap);
          if (fresh) setFamiliaDetalle(fresh);
        }, 150);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
  };

  const [historial, setHistorial] = useState(null);

  const startEdit = async (p) => {
    setEditing(p);
    // Refrescar la ficha completa (incluye desglose_historial reconstruido desde lotes si hace falta)
    api.get(`/productos/${p.id}`).then(({ data }) => setEditing((cur) => (cur && cur.id === p.id ? { ...cur, ...data } : cur))).catch(() => {});
    // Jun/2026: la calculadora usa la TRM/% del contenedor de ORIGEN del producto
    // (no la TRM global de configuración, que puede estar desactualizada).
    api.get(`/productos/_/costeo-defaults?producto_id=${p.id}`).then(({ data }) => setCosteoDefaults(data)).catch(() => {});
    setForm(formDesde(p));
    setHistorial(null);
    setOpen(true);
    // Cargar historial de precios en paralelo (no bloquea el modal)
    api.get(`/productos/${p.id}/historial-precios`)
      .then(({ data }) => setHistorial(data))
      .catch(() => setHistorial({ historial: [], total_cambios: 0 }));
  };

  // Jun/2026: tras un desglose de empaque, la ficha abierta debe reflejar los
  // valores nuevos (costo, precios, stock) — si no, "Guardar" los pisaría con los viejos.
  const refrescarEditing = async (pid) => {
    try {
      const { data } = await api.get(`/productos/${pid}`);
      setEditing(data);
      setForm((f) => ({ ...formDesde(data), nombre: f.nombre, descripcion: f.descripcion, categoria: f.categoria, color: f.color, motivo: f.motivo }));
      api.get(`/productos/${pid}/historial-precios`).then(({ data: h }) => setHistorial(h)).catch(() => {});
      load();
    } catch { /* noop */ }
  };

  const archive = async (p) => {
    const msg = `¿Archivar el producto "${p.nombre}"?\n\n` +
      `• Deja de aparecer en la búsqueda de pedidos.\n` +
      `• Los pedidos anteriores NO se ven afectados.\n` +
      `• Stock actual: ${p.stock_actual ?? 0} ${p.unidad || "un"}.\n` +
      `• Puedes reactivarlo después desde el filtro "Archivados".`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/productos/${p.id}`); toast.success("Archivado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo"); }
  };

  // Jun/2026: ocultar/mostrar a la venta (solo director). Ocultos: vendedores y operadores
  // no los ven en productos, armador de pedidos, IA ni catálogos; el director sí puede venderlos.
  const toggleOcultoVenta = async (ids, oculto) => {
    if (!ids.length) return;
    if (oculto && !window.confirm(`¿Ocultar ${ids.length} producto(s) a la venta?\n\nVendedores y operadores dejarán de verlos en Productos, en el armador de pedidos y en los catálogos. Solo tú (director) podrás verlos y montarlos en pedidos.`)) return;
    try {
      const { data } = await api.post("/productos/_/ocultar-venta", { producto_ids: ids, oculto });
      toast.success(oculto ? `${data.modificados} producto(s) ocultos a la venta` : `${data.modificados} producto(s) visibles de nuevo`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar la visibilidad"); }
  };


  const eliminarFamiliaCompleta = async () => {
    if (!eliminarFamiliaTarget) return;
    setEliminandoFamilia(true);
    try {
      const { data } = await api.post("/productos/familias/eliminar", {
        familia_key: eliminarFamiliaTarget.familiaKey,
        motivo: eliminarFamiliaMotivo.trim() || "Eliminación de grupo completo",
      });
      toast.success(`Grupo eliminado: ${data.archivados} producto${data.archivados === 1 ? "" : "s"} archivado${data.archivados === 1 ? "" : "s"}`);
      setEliminarFamiliaTarget(null);
      setEliminarFamiliaMotivo("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el grupo");
    } finally {
      setEliminandoFamilia(false);
    }
  };

  const convertirPadreAFamilia = async (padre) => {
    const msg = `¿Convertir "${padre.nombre}" al formato de grupo nuevo?\n\n` +
      `• Los ${padre.variantes_count || 0} colores/ítems quedarán intactos con su stock.\n` +
      `• Ganarás la vista bonita: click al grupo → modal con fotos, precios y acciones.\n` +
      `• NO se elimina nada — sólo se archiva el "padre viejo" que era un contenedor visual.\n\n` +
      `Esta acción es segura y reversible desde la BD.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/productos/${padre.id}/convertir-a-familia`);
      toast.success(`Grupo migrado: ${data.convertidas} ítems ahora usan el nuevo formato`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo convertir");
    }
  };

  const [adjusting, setAdjusting] = useState(false);
  const submitAdjust = async (e) => {
    e.preventDefault();
    setAdjusting(true);
    try {
      await api.post(`/productos/${adjustTarget.id}/ajustar-stock`, {
        cantidad: Number(adjust.cantidad),
        motivo: adjust.motivo,
      });
      toast.success("Stock ajustado");
      setAdjustTarget(null); setAdjust({ cantidad: "", motivo: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo ajustar"); }
    finally { setAdjusting(false); }
  };

  return (
    <div>
      {offlineMeta && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900 flex items-center gap-2" data-testid="offline-banner-productos">
          <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          <span><b>Sin conexión</b> — mostrando {offlineMeta.count} productos guardados hace {offlineMeta.ageMinutes} min. Reconecta para ver stock/precios actualizados.</span>
        </div>
      )}
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Catálogo</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground mt-2">Productos</h1>
          <p className="text-muted-foreground mt-2">
            {isDirector || canEdit || canCreate
              ? "Gestione su catálogo, precios y niveles de inventario."
              : "Consulte el catálogo, precios y stock disponible para armar pedidos."}
          </p>
        </div>
        <div className="flex gap-2">
          <PDFExportButton
            endpoint="productos"
            filename="beem-productos"
            label="Exportar PDF"
            testid="export-productos-pdf"
            hidden
            openSignal={pdfSignal}
            fields={[
              { key: "categoria", label: "Categoría", type: "select", optionsFrom: "categorias" },
              { key: "solo_con_stock", label: "Solo productos con stock", type: "checkbox" },
              { key: "solo_bajo_minimo", label: "Solo bajo mínimo", type: "checkbox" },
            ]}
          />
          {canCreate && (
            <Button onClick={() => setContainerWizardOpen(true)} variant="outline" className="h-11" data-testid="import-contenedor-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Recibir contenedor
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); api.get("/productos/_/costeo-defaults").then(({ data }) => setCosteoDefaults(data)).catch(() => {}); }} data-testid="new-producto-btn" className="bg-primary hover:bg-primary/90 h-11">
              <Plus className="w-4 h-4 mr-2" /> Nuevo producto
            </Button>
          )}
          <AccionesMenu
            testid="productos-mas-menu"
            items={[
              { label: "Exportar PDF", icon: Download, onClick: () => setPdfSignal((n) => n + 1), testid: "export-productos-pdf", hidden: !isDirector },
              { label: "Contenedores recibidos", icon: FileSpreadsheet, onClick: () => setHistContOpen(true), testid: "historial-contenedores-btn", hidden: !isDirector, title: "Ver contenedores importados y eliminar si fue erróneo" },
              { label: "Importar inventario Beem", icon: Upload, onClick: () => setLegacyBeemOpen(true), testid: "import-productos-btn", hidden: !canCreate, title: "Importa el inventario oficial de Beem (.xlsx)" },
              { label: "Crear grupo", icon: Layers, onClick: () => { setNuevoGrupoItems(new Set()); setNuevoGrupoNombre(""); setNuevoGrupoBuscar(""); setNuevoGrupoOpen(true); }, testid: "crear-grupo-btn", hidden: !canCreate, title: "Crea un grupo (ej. 'Cinta 4cm') y agrega variantes" },
            ]}
          />
        </div>
      </header>

      <UsageTip storageKey="productos-v2" steps={(() => {
        // Tips adaptados al rol/permisos del usuario:
        //  - Director / quien edite productos: ve todo (costeo + recibir contenedor).
        //  - Quien pueda ajustar stock: ve la nota de contenedor pero adaptada.
        //  - Vendedor puro (solo lectura): tips centrados en consulta y cotización.
        const tips = [];
        if (isDirector || canEdit) {
          tips.push("Cada producto tiene <b>costo real</b>, <b>precio mínimo</b> y <b>precio sugerido</b>. El costo real = base × (1 + %gastos).");
        } else {
          tips.push("Cada producto tiene <b>precio mínimo</b> autorizado y <b>precio sugerido</b>. Usa el sugerido en tus cotizaciones.");
        }
        if (isDirector || canCreate) {
          tips.push("Al <b>recibir contenedor</b> puedes revisar producto por producto y decidir si aplicas el nuevo precio o solo sumas stock.");
        } else if (canAdjustStock) {
          tips.push("Puedes <b>ajustar el stock</b> cuando llegue el inventario, dejando siempre un motivo trazable.");
        } else {
          tips.push("El <b>stock disponible</b> se actualiza automáticamente al armar pedidos y al recibir contenedores.");
        }
        // Extra info útil para todos:
        tips.push("Usa el filtro de arriba para ver rápido lo que está <b>sin stock</b>, <b>bajo el mínimo</b> o con stock OK.");
        return tips;
      })()} />

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
          <Input placeholder="Buscar por nombre, código (EF26-192), SKU..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" data-testid="search-producto" />
        </div>
      </div>

      {/* Filtros compactos (Jun/2026: menos chips, menos ruido visual) */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <FiltroMenu label="Stock" value={filtroStock} onChange={setFiltroStock} neutro="todos" testid="filtro-stock" opciones={[
          { v: "todos", label: "Todos" },
          { v: "sin_stock", label: "Sin stock", dot: "rose" },
          { v: "bajo_stock", label: "Bajo el mínimo", dot: "amber" },
          { v: "con_stock", label: "Con stock OK", dot: "emerald" },
        ]} />
        <FiltroMenu label="Problemas" value={filtroProblema} onChange={setFiltroProblema} neutro="ninguno" testid="filtro-problema" opciones={[
          { v: "ninguno", label: "Ninguno" },
          { v: "sin_categoria", label: "Sin categoría", dot: "rose" },
          { v: "sin_codigo", label: "Sin código físico", dot: "amber" },
          { v: "sin_sku", label: "Sin SKU", dot: "fuchsia" },
        ]} />
        <div className="ml-auto flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={agruparEstilos}
              onChange={(e) => setAgruparEstilos(e.target.checked)}
              className="w-4 h-4"
              data-testid="toggle-agrupar-estilos"
            />
            <Layers className="w-4 h-4 text-primary" />
            <span className="font-semibold text-foreground">Agrupar por estilo</span>
          </label>
        </div>
      </div>

      {/* Barra bulk STICKY: se pega al top del viewport cuando el usuario
          scrollea, así siempre está a la mano al seleccionar productos.
          Feb/2026: sticky por petición explícita — usuario perdía la barra
          al bajar para marcar checkboxes de familias/variantes. */}
      {canBulk && selected.size > 0 && (
        <div className="sticky top-2 z-40 mb-3 bg-primary/95 text-primary-foreground backdrop-blur-md border border-primary/40 rounded-full px-3 py-1.5 flex items-center gap-2 flex-wrap shadow-lg animate-slide-up" data-testid="bulk-bar">
          <span className="flex items-center gap-1.5 pl-1 pr-2">
            <span className="w-6 h-6 rounded-full bg-white text-primary flex items-center justify-center text-[11px] font-bold tabular-nums">{selected.size}</span>
            <span className="text-xs font-semibold hidden sm:inline">seleccionado{selected.size === 1 ? "" : "s"}</span>
          </span>
          <span className="w-px h-5 bg-white/30 mx-0.5" />
          <select
            value={bulkRegla}
            onChange={(e) => setBulkRegla(e.target.value)}
            className="h-8 bg-white/10 hover:bg-white/20 border-0 rounded-full px-3 text-xs font-medium text-white focus:outline-none focus:ring-2 focus:ring-white/50 cursor-pointer appearance-none"
            data-testid="bulk-regla-select"
            style={{ backgroundImage: "none" }}
            title="Regla de comisión"
          >
            <option value="" className="text-foreground">Regla…</option>
            {reglas.map((r) => <option key={r.id} value={r.id} className="text-foreground">{r.nombre}</option>)}
          </select>
          <button
            disabled={!bulkRegla || bulkAssigning}
            onClick={async () => {
              setBulkAssigning(true);
              try {
                const { data } = await api.post("/productos/_/asignar-regla", {
                  producto_ids: Array.from(selected),
                  regla_comision_id: bulkRegla,
                });
                const nombreRegla = reglas.find((r) => r.id === bulkRegla)?.nombre || "regla";
                toast.success(`${data.afectados} producto(s) asignados a "${nombreRegla}"`);
                setSelected(new Set());
                setBulkRegla("");
                load();
              } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo asignar"); }
              finally { setBulkAssigning(false); }
            }}
            className="h-8 px-3 rounded-full bg-white text-primary text-xs font-semibold hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
            data-testid="bulk-assign-btn"
            title="Aplicar regla"
          >
            <Check className="w-3.5 h-3.5" /> {bulkAssigning ? "…" : "Aplicar"}
          </button>
          <span className="w-px h-5 bg-white/30 mx-0.5" />
          {isDirector && (
            <>
              <button onClick={() => toggleOcultoVenta(Array.from(selected), true)}
                className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors"
                data-testid="bulk-ocultar-btn" title="Ocultar a la venta: vendedores y operadores no los verán en productos, pedidos ni catálogos. Solo el director puede venderlos.">
                <EyeOff className="w-3.5 h-3.5" /><span className="hidden sm:inline">Ocultar</span>
              </button>
              <button onClick={() => toggleOcultoVenta(Array.from(selected), false)}
                className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors"
                data-testid="bulk-mostrar-btn" title="Volver a mostrar a la venta">
                <Eye className="w-3.5 h-3.5" /><span className="hidden sm:inline">Mostrar</span>
              </button>
              <span className="w-px h-5 bg-white/30 mx-0.5" />
            </>
          )}
          <button
            onClick={() => {
              setBulkPrecios({
                modo: "fijo",
                aplicar_sugerido: true,
                aplicar_minimo: true,
                precio_sugerido_valor: "",
                precio_minimo_valor: "",
                porcentaje: "",
                motivo: "",
              });
              setBulkPreciosOpen(true);
            }}
            className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors"
            data-testid="bulk-precios-btn"
            title={`Editar precios de los ${selected.size} productos`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Precios</span>
          </button>
          <button
            onClick={() => { setBulkCodigoValor(""); setBulkCodigoOpen(true); }}
            className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors"
            data-testid="bulk-codigo-btn"
            title={`Asignar código real a los ${selected.size} productos`}
          >
            <Wand2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Código</span>
          </button>
          <button
            onClick={() => { setBulkEmpaqueValor("unidad"); setBulkEmpaqueOpen(true); }}
            className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors"
            data-testid="bulk-empaque-btn"
            title={`Asignar unidad de empaque a los ${selected.size} productos`}
          >
            <Package className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Empaque</span>
          </button>
          {isDirector && (
            <button
              onClick={() => setBulkDesglosarOpen(true)}
              className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors"
              data-testid="bulk-desglosar-btn"
              title={`Desglosar empaque ×N (cantidad ×N, costo ÷N) en los ${selected.size} productos`}
            >
              <Boxes className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Desglosar ×N</span>
            </button>
          )}
          <button
            disabled={selected.size < 2}
            onClick={() => {
              setNuevoGrupoItems(new Set(selected));
              setNuevoGrupoNombre("");
              setNuevoGrupoBuscar("");
              setNuevoGrupoOpen(true);
            }}
            className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-semibold flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bulk-agrupar-btn"
            title={selected.size < 2 ? "Selecciona al menos 2 productos" : `Agrupar ${selected.size} productos`}
          >
            <Plus className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Agrupar</span>
          </button>
          <button
            onClick={() => { setSelected(new Set()); setBulkRegla(""); }}
            className="h-8 w-8 rounded-full hover:bg-white/20 flex items-center justify-center ml-auto"
            data-testid="bulk-clear-btn"
            title="Limpiar selección"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl beem-table">
        {(() => {
          // Filtrado client-side: la búsqueda es INSTANTÁNEA (sin round-trip al backend).
          // Ahora con fuzzy multi-palabra (acentos, orden libre, "dos" → "2", "cm" ↔ "centimetros").
          const qTrim = q.trim();
          const itemsFiltrados = items.filter((p) => {
            if (qTrim) {
              const haystack = [
                p.nombre || "",
                p.sku || "",
                p.familia_key || "",
                p.color || "",
                p.descripcion || "",
                p.categoria || "",
                p.codigo || "",
              ].join(" ");
              if (!fuzzyMatch(haystack, qTrim)) return false;
            }
            if (filtroStock === "todos") return true;
            // "Disponible" = stock físico menos lo reservado por pedidos vivos.
            // Un producto con stock=5 pero 5 unidades reservadas está EFECTIVAMENTE agotado,
            // así que debe aparecer en "Sin stock" (no en "Bajo el mínimo").
            const disp = (p.stock_actual || 0) - (p.stock_reservado || 0);
            const minimo = p.stock_minimo || 0;
            if (filtroStock === "sin_stock") return disp <= 0;
            if (filtroStock === "bajo_stock") return disp > 0 && disp <= minimo;
            if (filtroStock === "con_stock") return disp > minimo;
            return true;
          }).filter((p) => {
            // Feb/2026: filtro de "problemas" tras import de contenedor
            if (filtroProblema === "ninguno") return true;
            if (filtroProblema === "sin_categoria") return !p.categoria || !String(p.categoria).trim();
            if (filtroProblema === "sin_codigo") return !p.codigo || !String(p.codigo).trim();
            if (filtroProblema === "sin_sku") return !p.sku || !String(p.sku).trim();
            return true;
          });

          // Cuando el toggle "Agrupar por estilo" está activo, agrupamos por familia_key.
          // Productos sin familia_key usan el nombre antes del " - " como fallback (igual que el catálogo público).
          // Familias con 2+ variantes se colapsan en una fila padre expandible.
          const familias = new Map();
          const sueltos = [];
          const _familiaKeyDe = (p) => {
            const raw = (p.familia_key || "").trim();
            if (raw) return raw;
            const nom = (p.nombre || "").trim();
            const idx = nom.lastIndexOf(" - ");
            return idx > 0 ? nom.slice(0, idx).toLowerCase() : "";
          };
          if (agruparEstilos) {
            for (const p of itemsFiltrados) {
              if (p.es_padre) { sueltos.push(p); continue; }
              const key = _familiaKeyDe(p);
              if (!key) { sueltos.push(p); continue; }
              if (!familias.has(key)) familias.set(key, []);
              familias.get(key).push(p);
            }
            // Familias con 1 sola variante → tratarlas como sueltas (no colapsar)
            for (const [k, arr] of familias.entries()) {
              if (arr.length < 2) {
                sueltos.push(...arr);
                familias.delete(k);
              }
            }
          } else {
            sueltos.push(...itemsFiltrados);
          }

          return (<>
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
          <div className={`${canBulk ? "col-span-3" : "col-span-3"} flex items-center gap-3`}>
            {canBulk && (
              <input
                type="checkbox"
                checked={itemsFiltrados.length > 0 && itemsFiltrados.every((p) => selected.has(p.id))}
                onChange={(e) => {
                  if (e.target.checked) setSelected(new Set(itemsFiltrados.map((p) => p.id)));
                  else setSelected(new Set());
                }}
                className="w-4 h-4 flex-shrink-0"
                data-testid="bulk-select-all"
                title="Seleccionar todos los visibles"
              />
            )}
            <span>Producto</span>
          </div>
          <div className="col-span-2">Código real</div>
          <div className="col-span-2 text-right">Sugerido</div>
          <div className="col-span-1 text-right">Mínimo</div>
          <div className="col-span-2 text-right">Stock</div>
          <div className="col-span-2 text-right">Acciones</div>
        </div>
        {itemsFiltrados.length === 0 && (
          <div className="px-6 py-16 text-center text-muted-foreground/70">
            <Package className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="mt-3 text-sm">
              {items.length === 0
                ? "No hay productos aún. Crea el primero para empezar el catálogo."
                : `No hay productos en el filtro "${{
                    sin_stock: "Sin stock",
                    bajo_stock: "Bajo el mínimo",
                    con_stock: "Con stock OK",
                  }[filtroStock] || filtroStock}". Cambia el filtro o revisa el catálogo.`}
            </div>
          </div>
        )}
        {/* Familias agrupadas por estilo (familia_key) */}
        {agruparEstilos && Array.from(familias.entries()).map(([familiaKey, variantes]) => {
          const stockTotal = variantes.reduce((sum, v) => sum + ((v.stock_actual || 0) - (v.stock_reservado || 0)), 0);
          const precios = variantes.map((v) => v.precio_sugerido || v.precio_base || 0).filter((x) => x > 0);
          const precioMin = precios.length ? Math.min(...precios) : 0;
          const precioMax = precios.length ? Math.max(...precios) : 0;
          const primero = variantes[0];
          const familiaNombre = primero.nombre?.split(" - ")[0] || familiaKey;
          const open = !!familiaAbierta[familiaKey];
          const colores = variantes.map((v) => (v.color || v.nombre?.split(" - ")[1] || "").trim()).filter(Boolean);
          const anyLow = variantes.some((v) => ((v.stock_actual || 0) - (v.stock_reservado || 0)) <= (v.stock_minimo || 0));
          // Miniaturas de las primeras variantes con foto (para preview visual del estilo)
          const variantesConFoto = variantes.filter((v) => v.imagen_path).slice(0, 6);
          const fotosExtra = variantes.filter((v) => v.imagen_path).length - variantesConFoto.length;
          return (
            <div key={`familia-${familiaKey}`}>
              <div
                className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/60 items-center hover:bg-primary/[0.04] transition-colors bg-primary/[0.02] cursor-pointer"
                onClick={() => setFamiliaDetalle({ familiaKey, familiaNombre, variantes })}
                data-testid={`familia-row-${familiaKey.substring(0, 24)}`}
              >
                <div className="col-span-3 flex items-center gap-2">
                  <button
                    className="p-0.5 rounded hover:bg-primary/10 text-primary flex-shrink-0"
                    title={open ? "Contraer" : "Ver colores en fila"}
                    onClick={(e) => { e.stopPropagation(); setFamiliaAbierta({ ...familiaAbierta, [familiaKey]: !open }); }}
                    data-testid={`familia-toggle-${familiaKey.substring(0, 24)}`}
                  >
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  {canBulk && (() => {
                    // Checkbox para seleccionar/deseleccionar TODAS las variantes de esta familia
                    const allIds = variantes.map((v) => v.id);
                    const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
                    const someSelected = allIds.some((id) => selected.has(id));
                    return (
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }}
                        onChange={(e) => {
                          const next = new Set(selected);
                          if (e.target.checked) allIds.forEach((id) => next.add(id));
                          else allIds.forEach((id) => next.delete(id));
                          setSelected(next);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 flex-shrink-0"
                        data-testid={`familia-select-${familiaKey.substring(0, 24)}`}
                        title={allSelected ? "Deseleccionar toda la familia" : "Seleccionar toda la familia"}
                      />
                    );
                  })()}
                  {primero.imagen_path ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setLightbox({ src: `${API}/catalogo/img/${primero.id}?w=800&v=${primero.imagen_actualizada_at || 0}`, alt: familiaNombre }); }}
                      className="w-10 h-10 rounded-md overflow-hidden border border-border bg-muted flex-shrink-0 hover:ring-2 hover:ring-primary cursor-zoom-in transition-all"
                      title="Ver imagen en grande"
                      data-testid={`familia-img-${familiaKey.substring(0, 24)}`}
                    >
                      <img src={`${API}/catalogo/img/${primero.id}?w=200&v=${primero.imagen_actualizada_at || 0}`} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <div className="w-10 h-10 rounded-md border border-dashed border-border/60 flex items-center justify-center text-muted-foreground/40 flex-shrink-0" title="Sin foto">
                      <ImageIcon className="w-4 h-4" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground truncate flex items-center gap-2">
                      <span className="truncate">{familiaNombre}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground uppercase font-semibold flex items-center gap-0.5 flex-shrink-0">
                        <Layers className="w-2.5 h-2.5" /> {variantes.length}
                      </span>
                    </div>
                    {/* Swatches de color (visual rápido) + strip de fotos */}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {colores.slice(0, 10).map((c, i) => (
                        <ColorSwatch key={`${c}-${i}`} nombre={c} size={14} title={c} />
                      ))}
                      {colores.length > 10 && (
                        <span className="text-[10px] text-muted-foreground font-semibold">+{colores.length - 10}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-span-2">
                  {/* Feb/2026: mostrar el código real de la familia (todos los
                      variantes comparten el mismo por diseño). Si es null, no
                      mostramos nada (los variantes se ven al expandir). */}
                  {(() => {
                    const codigosUnicos = [...new Set(variantes.map((v) => v.codigo).filter(Boolean))];
                    if (codigosUnicos.length === 0) return null;
                    if (codigosUnicos.length === 1) {
                      return (
                        <div className="font-mono font-semibold text-indigo-700 text-sm" title="Código real (marcado en cajas)">
                          {codigosUnicos[0]}
                        </div>
                      );
                    }
                    // Múltiples códigos distintos (lote a lote)
                    return (
                      <div className="flex flex-wrap gap-1" title={`${codigosUnicos.length} códigos reales distintos`}>
                        {codigosUnicos.slice(0, 3).map((c) => (
                          <span key={c} className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-semibold">{c}</span>
                        ))}
                        {codigosUnicos.length > 3 && <span className="text-[10px] text-muted-foreground">+{codigosUnicos.length - 3}</span>}
                      </div>
                    );
                  })()}
                  {/* Photo strip preview (compact) */}
                  {variantesConFoto.length > 0 ? (
                    <div className="flex items-center -space-x-1.5 mt-1">
                      {variantesConFoto.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setLightbox({ src: `${API}/catalogo/img/${v.id}?w=800&v=${v.imagen_actualizada_at || 0}`, alt: v.color || v.nombre }); }}
                          className="w-6 h-6 rounded-md overflow-hidden border-2 border-card bg-muted flex-shrink-0 hover:ring-2 hover:ring-primary cursor-zoom-in transition-all"
                          title={`Ver ${v.color || v.nombre} en grande`}
                          data-testid={`familia-variante-img-${v.id}`}
                        >
                          <img src={`${API}/catalogo/img/${v.id}?w=100&v=${v.imagen_actualizada_at || 0}`} alt="" loading="lazy" className="w-full h-full object-cover" />
                        </button>
                      ))}
                      {fotosExtra > 0 && (
                        <div className="w-6 h-6 rounded-md border-2 border-card bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                          +{fotosExtra}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="col-span-2 text-right">
                  <div className="text-sm font-semibold text-foreground tabular-nums">
                    {precioMin === precioMax ? formatCOP(precioMin) : `${formatCOP(precioMin)} - ${formatCOP(precioMax)}`}
                  </div>
                </div>
                <div className="col-span-1 text-right text-xs text-muted-foreground tabular-nums">—</div>
                <div className="col-span-2 text-right">
                  <div className={`text-sm font-semibold tabular-nums ${anyLow ? "text-amber-600" : "text-foreground"}`}>
                    {formatNumber(stockTotal)}
                  </div>
                  <div className="text-[10px] text-primary/70">total del estilo</div>
                </div>
                <div className="col-span-2 flex justify-end gap-1 text-xs">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFamiliaDetalle({ familiaKey, familiaNombre, variantes }); }}
                    className="px-2.5 py-1 rounded-md bg-primary/10 hover:bg-primary/20 text-primary font-semibold"
                    data-testid={`familia-ver-detalle-${familiaKey.substring(0, 24)}`}
                  >
                    Ver detalle
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFamiliaAbierta({ ...familiaAbierta, [familiaKey]: !open }); }}
                    className="px-2 py-1 rounded-md hover:bg-muted text-muted-foreground"
                    data-testid={`familia-ver-colores-${familiaKey.substring(0, 24)}`}
                    title={open ? "Contraer fila" : "Expandir en fila"}
                  >
                    {open ? "Contraer" : "En fila"}
                  </button>
                  {canArchive && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEliminarFamiliaTarget({ familiaKey, familiaNombre, count: variantes.length });
                      }}
                      className="p-1.5 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600"
                      title="Eliminar grupo completo"
                      data-testid={`familia-eliminar-${familiaKey.substring(0, 24)}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {open && variantes.map((v) => {
                const disp = (v.stock_actual || 0) - (v.stock_reservado || 0);
                const vLow = disp <= (v.stock_minimo || 0);
                const vChecked = selected.has(v.id);
                const color = v.color || v.nombre?.split(" - ")[1]?.trim() || "";
                return (
                  <div key={v.id}
                    className={`grid grid-cols-12 gap-4 pl-14 pr-6 py-3 border-b border-border/40 items-center bg-muted/30 hover:bg-muted/50 transition-colors ${vChecked ? "bg-primary/5" : ""}`}
                    data-testid={`variante-row-${v.id}`}
                  >
                    <div className="col-span-3 flex items-center gap-2">
                      {canBulk && (
                        <input
                          type="checkbox"
                          checked={vChecked}
                          onChange={(e) => {
                            const next = new Set(selected);
                            if (e.target.checked) next.add(v.id); else next.delete(v.id);
                            setSelected(next);
                          }}
                          className="w-3.5 h-3.5 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <ColorSwatch nombre={color} size={12} className="flex-shrink-0" />
                      <div className="text-sm text-foreground truncate">
                        {color || v.nombre}
                      </div>
                    </div>
                    <div className="col-span-2 text-xs text-muted-foreground font-mono truncate" title={v.codigo ? "Código real (packing list)" : ""}>
                      {v.codigo ? (
                        <span className="text-indigo-700 font-semibold">{v.codigo}</span>
                      ) : (
                        <span>{v.sku || "—"}</span>
                      )}
                    </div>
                    <div className="col-span-2 text-right text-sm tabular-nums">{formatCOP(v.precio_sugerido || v.precio_base || 0)}</div>
                    <div className="col-span-1 text-right text-xs text-muted-foreground tabular-nums">{formatCOP(v.precio_minimo || 0)}</div>
                    <div className="col-span-2 text-right">
                      <div className={`text-sm tabular-nums ${vLow ? "text-rose-600 font-semibold" : "text-foreground"}`}>{formatNumber(disp)}</div>
                    </div>
                    <div className="col-span-2 flex justify-end gap-1">
                      {canAdjustStock && (
                        <button onClick={() => { setAdjustTarget(v); setAdjust({ cantidad: "", motivo: "" }); }}
                          className="text-xs text-primary hover:underline"
                          data-testid={`ajustar-variante-${v.id}`}
                        >Ajustar</button>
                      )}
                      {canCreate && (
                        <button onClick={() => { setEditing(v); setForm({ ...empty, ...v }); setOpen(true); }}
                          className="text-xs text-muted-foreground hover:text-foreground ml-2"
                          data-testid={`editar-variante-${v.id}`}
                        >Editar</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {sueltos.map((p) => {
          const disponible = (p.stock_actual || 0) - (p.stock_reservado || 0);
          const low = disponible <= (p.stock_minimo || 0);
          const inactive = p.activo === false;
          const isChecked = selected.has(p.id);
          const reglaActual = reglas.find((r) => r.id === (p.regla_comision_id || "default"));
          const sinRegla = !reglaActual;
          return (
            <div key={p.id}>
            <div className={`grid grid-cols-12 gap-4 px-6 py-4 border-b border-border/60 items-center hover:bg-muted/40 transition-colors ${inactive ? "opacity-60" : ""} ${isChecked ? "bg-primary/5" : ""} ${p.es_padre ? "bg-primary/[0.03]" : ""}`} data-testid={`producto-row-${p.id}`}>
              <div className="col-span-3 flex items-center gap-2">
                {p.es_padre ? (
                  <button
                    onClick={() => toggleVariantes(p)}
                    className="p-0.5 rounded hover:bg-primary/10 text-primary flex-shrink-0"
                    data-testid={`expand-${p.id}`}
                    title="Ver variantes"
                  >
                    {expanded[p.id]?.open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                ) : (
                  <span className="w-5 flex-shrink-0" />
                )}
                {canBulk && (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(p.id); else next.delete(p.id);
                      setSelected(next);
                    }}
                    className="w-4 h-4 flex-shrink-0"
                    data-testid={`bulk-check-${p.id}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                {p.imagen_path ? (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setLightbox({ src: `${API}/catalogo/img/${p.id}?w=800&v=${p.imagen_actualizada_at || 0}`, alt: p.nombre }); }}
                    className="w-10 h-10 rounded-md overflow-hidden border border-border bg-muted flex-shrink-0 group relative"
                    data-testid={`thumb-${p.id}`}
                    title="Ver foto grande"
                  >
                    <img src={`${API}/catalogo/img/${p.id}?w=200&v=${p.imagen_actualizada_at || 0}`} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                    <div className="absolute inset-0 bg-foreground/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ) : (
                  <div className="w-10 h-10 rounded-md border border-dashed border-border/60 flex items-center justify-center text-muted-foreground/40 flex-shrink-0" title="Sin foto">
                    <ImageIcon className="w-4 h-4" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground flex items-center gap-2">
                    <span className="truncate">{p.nombre}</span>
                    {p.es_padre && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase font-semibold flex items-center gap-0.5 flex-shrink-0" title="Producto padre con variantes">
                        <Layers className="w-2.5 h-2.5" /> {p.variantes_count || 0}
                      </span>
                    )}
                    {inactive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-muted-foreground">inactivo</span>}
                    {p.oculto_venta && isDirector && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); toggleOcultoVenta([p.id], false); }}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold flex items-center gap-0.5 flex-shrink-0 hover:bg-amber-200"
                        title={`Oculto a la venta (solo tú lo ves)${p.oculto_venta_por ? ` · por ${p.oculto_venta_por}` : ""}. Click para volver a mostrarlo.`} data-testid={`oculto-badge-${p.id}`}>
                        <EyeOff className="w-2.5 h-2.5" /> oculto
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {p.unidad}{p.categoria ? ` · ${p.categoria}` : ""}
                    {reglaActual && <span className="ml-1"> · <Award className="w-3 h-3 inline text-primary/60" /> {reglaActual.nombre}</span>}
                    {sinRegla && (
                      <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold uppercase tracking-wide" title="Este producto no tiene regla de comisión asignada. Asígnale una desde 'Reglas de comisión' o al editar el producto.">
                        <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-0.5" />SIN REGLA
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-span-2 text-sm text-muted-foreground font-mono">
                {p.codigo ? (
                  <span className="font-semibold text-indigo-700" title="Código real (marcado en las cajas del packing list)">{p.codigo}</span>
                ) : (
                  <span title="Sin código real — se muestra referencia interna" className="italic text-muted-foreground/60">{p.sku || "—"}</span>
                )}
              </div>
              <div className="col-span-2 text-right">
                <div className="text-sm font-semibold text-foreground tabular-nums">{formatCOP(p.precio_sugerido || p.precio_base || 0)}</div>
                {canVerCosto && p.costo !== undefined && p.costo !== null && (
                  <div className="text-[10px] text-muted-foreground/70 tabular-nums">costo {formatCOP(p.costo)}</div>
                )}
              </div>
              <div className="col-span-1 text-right text-xs text-muted-foreground tabular-nums">{formatCOP(p.precio_minimo || 0)}</div>
              <div className="col-span-2 text-right">
                <div className={`text-sm font-semibold tabular-nums ${low ? "text-rose-600" : "text-foreground"}`}>{formatNumber(p.es_padre && p.stock_agrupado !== undefined ? p.stock_agrupado : disponible)}</div>
                {p.es_padre && p.stock_agrupado !== undefined && <div className="text-[10px] text-primary/70">agregado (padre + variantes)</div>}
                {!p.es_padre && p.stock_reservado > 0 && <div className="text-[10px] text-muted-foreground">{p.stock_reservado} reservado{p.stock_reservado === 1 ? "" : "s"}</div>}
                {!p.es_padre && (
                  <StockMinimoInline
                    producto={p}
                    canEdit={canEditStockMin}
                    onUpdated={(pid, nuevo, extra) => setItems((xs) => xs.map((it) => it.id === pid ? { ...it, stock_minimo: nuevo, stock_minimo_manual: true, ...(extra || {}) } : it))}
                  />
                )}
                {!p.es_padre && low && (p.stock_minimo || 0) > 0 && (
                  <div className="text-[10px] text-rose-600 font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Bajo el mínimo</div>
                )}
                {!p.es_padre && (low || disponible <= 0) && (isDirector || canEditStockMin || canAdjustStock) && <ReordenHint producto={p} disponible={disponible} />}
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                {canAdjustStock && (
                  <button onClick={() => setAdjustTarget(p)} className="p-2 rounded-md hover:bg-accent text-muted-foreground/70 hover:text-primary" title="Ajustar stock" data-testid={`adjust-${p.id}`}>
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                )}
                {canEdit && !p.es_padre && (
                  <button onClick={() => startEdit(p)} className="p-2 rounded-md hover:bg-muted text-muted-foreground/70 hover:text-foreground/80" title="Editar" data-testid={`edit-producto-${p.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                {canEdit && p.es_padre && (
                  <button
                    onClick={() => convertirPadreAFamilia(p)}
                    className="p-2 rounded-md hover:bg-emerald-50 text-emerald-700 hover:text-emerald-800 border border-emerald-200 bg-emerald-50/40"
                    title="Convertir a grupo con vista nueva (click para abrir modal con fotos/colores)"
                    data-testid={`convertir-padre-${p.id}`}
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                )}
                {canArchive && (
                  <button
                    onClick={() => {
                      if (p.es_padre) {
                        // Padre virtual: eliminar el grupo completo (padre + variantes)
                        setEliminarFamiliaTarget({
                          familiaKey: (p.familia_key || p.nombre || "").toUpperCase(),
                          familiaNombre: p.nombre,
                          count: (p.variantes_count || 0) + 1,
                        });
                      } else if (window.confirm(`¿Eliminar "${p.nombre}"?\n\nEl producto se archivará y dejará de aparecer en la lista principal. Puedes recuperarlo desde el filtro "Archivados".`)) {
                        archive(p);
                      }
                    }}
                    className="p-2 rounded-md hover:bg-rose-50 text-muted-foreground/70 hover:text-rose-600"
                    title={p.es_padre ? "Eliminar grupo completo" : "Eliminar producto"}
                    data-testid={`delete-producto-${p.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {!canEdit && !canArchive && !canAdjustStock && (
                  <span className="text-xs text-muted-foreground/70">solo lectura</span>
                )}
              </div>
            </div>
            {/* Variantes expandibles */}
            {p.es_padre && expanded[p.id]?.open && (
              <div className="border-b border-border/60 bg-muted/20" data-testid={`variantes-${p.id}`}>
                {expanded[p.id].loading ? (
                  <div className="px-14 py-3 text-xs text-muted-foreground">Cargando variantes...</div>
                ) : expanded[p.id].variantes.length === 0 ? (
                  <div className="px-14 py-3 text-xs text-muted-foreground">Sin variantes agrupadas todavía.</div>
                ) : (
                  expanded[p.id].variantes.map((v) => (
                    <div key={v.id} className="grid grid-cols-12 gap-4 px-6 py-2 border-t border-border/40 items-center" data-testid={`variante-row-${v.id}`}>
                      <div className="col-span-3 flex items-center gap-2 pl-10">
                        <div className="w-1 h-6 rounded-full bg-primary/30" />
                        <div className="min-w-0">
                          <div className="text-sm text-foreground truncate">{v.nombre}</div>
                          <div className="text-[10px] text-primary font-medium">{v.variante_label || "variante"}</div>
                        </div>
                      </div>
                      <div className="col-span-2 text-xs text-muted-foreground font-mono truncate">
                        {v.codigo ? (
                          <span className="text-indigo-700 font-semibold" title="Código real (packing list)">{v.codigo}</span>
                        ) : (
                          <span>{v.sku || "—"}</span>
                        )}
                      </div>
                      <div className="col-span-2 text-right text-xs text-muted-foreground tabular-nums">{formatCOP(v.precio_sugerido || 0)}</div>
                      <div className="col-span-1 text-right text-xs text-muted-foreground/70 tabular-nums">{formatCOP(v.precio_minimo || 0)}</div>
                      <div className="col-span-2 text-right text-xs text-muted-foreground tabular-nums">{formatNumber((v.stock_actual || 0) - (v.stock_reservado || 0))}</div>
                      <div className="col-span-2 flex justify-end gap-1">
                        {canEdit && (
                          <button onClick={() => startEdit(v)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground/70 hover:text-foreground/80" title="Editar variante" data-testid={`edit-var-${v.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
            </div>
          );
        })}
        </>);
        })()}
      </div>

      {/* Modal create/edit */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="producto-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4 mt-4">
            <div className="col-span-2">
              <Label>Nombre*</Label>
              <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} className="h-11 mt-1 uppercase" data-testid="producto-nombre" />
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-3">
              <div>
                <Label className="flex items-center gap-1.5">
                  Código real
                  <span className="text-[10px] font-normal text-muted-foreground italic">(cajas)</span>
                </Label>
                <Input
                  value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                  className="h-11 mt-1 font-mono text-indigo-700 font-semibold"
                  placeholder="Ej: EF26-148"
                  data-testid="producto-codigo"
                />
              </div>
              <div>
                <Label>SKU / Referencia interna</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="h-11 mt-1 font-mono" placeholder="Ej: CRN0037DO" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="h-11 mt-1" placeholder="Ej: Cintas, Papeles..." />
              </div>
            </div>
            <div>
              <Label>Unidad</Label>
              <select value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm">
                {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Color / Variante (diferenciador)</Label>
              <Input
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="h-11 mt-1"
                placeholder="Ej: Rojo, Talla M, 2cm, Rosa Pastel..."
                data-testid="producto-color"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Deja el nombre general del producto arriba (ej: "Cinta grosgrain"). Aquí solo va lo que diferencia esta variante del resto del grupo.
              </p>
            </div>
            {canVerCosto && (
              <div>
                <Label>Costo de referencia (COP, solo directores) <span className="font-normal text-muted-foreground">· la utilidad usa el costo FIFO de los lotes</span></Label>
                <MoneyInput value={form.costo} onValueChange={(v) => setForm({ ...form, costo: v })} className="h-11 mt-1" placeholder="Opcional" />
              </div>
            )}
            <div>
              <Label>Precio sugerido (COP)*</Label>
              <MoneyInput required value={form.precio_sugerido} onValueChange={(v) => setForm({ ...form, precio_sugerido: v })} className="h-11 mt-1" data-testid="producto-precio-sugerido" />
            </div>
            <div>
              <Label>Precio mínimo autorizado (COP)</Label>
              <MoneyInput value={form.precio_minimo} onValueChange={(v) => setForm({ ...form, precio_minimo: v })} className="h-11 mt-1" placeholder="Auto: 90% del sugerido" data-testid="producto-precio-minimo" />
            </div>
            {canVerCosto && (
              <CosteoCalculator
                defaults={costeoDefaults}
                onApply={({ costo, precio_minimo, precio_sugerido }) => {
                  setForm((f) => ({
                    ...f,
                    costo: String(costo),
                    precio_minimo: String(precio_minimo),
                    precio_sugerido: String(precio_sugerido),
                  }));
                  toast.success("Costo y precios calculados aplicados");
                }}
              />
            )}
            <div>
              <Label>Regla de comisión</Label>
              <select value={form.regla_comision_id} onChange={(e) => setForm({ ...form, regla_comision_id: e.target.value })} className="w-full h-11 mt-1 border border-border rounded-md px-3 bg-card text-sm">
                {reglas.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
              </select>
            </div>
            <div>
              <Label>Stock actual</Label>
              <Input type="number" min="0" step="1" value={form.stock_actual} onChange={(e) => setForm({ ...form, stock_actual: e.target.value })} className="h-11 mt-1" data-testid="producto-stock" />
            </div>
            <div>
              <Label>Stock minimo (alerta)</Label>
              <Input type="number" min="0" step="1" value={form.stock_minimo} onChange={(e) => setForm({ ...form, stock_minimo: e.target.value })} className="h-11 mt-1" />
            </div>
            {editing && isDirector && (
              <div className="col-span-2">
                <Label>Factor de empaque del proveedor (unidades de venta por paquete)</Label>
                <Input type="number" min="1" step="1" value={form.factor_empaque} onChange={(e) => setForm({ ...form, factor_empaque: e.target.value })} className="h-11 mt-1" placeholder="Vacío = el proveedor factura en la misma unidad que vendes" data-testid="producto-factor-empaque" />
                <div className="text-[11px] text-muted-foreground mt-1">Si el Excel del proveedor trae paquetes de N sub-paquetes (ej. moños ×20), el importador convertirá automáticamente cantidad ×N y costo ÷N. Para re-expresar lotes ya recibidos usa "Desglosar empaque" en la sección de lotes.</div>
              </div>
            )}
            <div className="col-span-2 flex items-center gap-3 p-3 bg-muted/40 rounded-lg border border-border">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} className="w-4 h-4" />
                <span className="text-sm font-medium">Producto activo</span>
              </label>
              <span className="text-xs text-muted-foreground">Si se desactiva, no aparecerá en pedidos nuevos.</span>
            </div>
            {editing && (
              <div className="col-span-2 border border-border rounded-lg p-3 bg-muted/20">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground mb-2">Foto del producto (para catálogo)</div>
                <ProductoImagenUploader
                  productoId={editing.id}
                  productoNombre={editing.nombre}
                  tieneImagen={editing.imagen_path}
                  onUpdated={() => load()}
                />
              </div>
            )}
            {editing && isDirector && (
              <>
                <DesgloseResumenCard producto={editing} onChanged={() => refrescarEditing(editing.id)} />
                <LotesInventarioSection productoId={editing.id} onDesglosado={() => refrescarEditing(editing.id)} />
              </>
            )}
            {editing && (
              <div className="col-span-2">
                <Label>Motivo del cambio (opcional pero recomendado)</Label>
                <Input value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} className="h-11 mt-1" placeholder="Ej: Ajuste por inflación, cambio de proveedor..." />
              </div>
            )}
            <div className="col-span-2">
              <Label className="flex items-center gap-1">
                Descripción <span className="text-xs font-normal text-muted-foreground">(opcional)</span>
              </Label>
              <textarea
                value={form.descripcion || ""}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value.toUpperCase() })}
                className="uppercase w-full mt-1 border border-border rounded-md px-3 py-2 text-sm min-h-[90px] focus:ring-2 focus:ring-primary/50 focus:outline-none"
                placeholder="Descripción del producto para catálogos, cotizaciones y pedidos. Ej: Cinta de razo doble faz, ideal para arreglos florales. Textura suave, no se deshila."
                maxLength={800}
                data-testid="input-descripcion"
              />
              <div className="text-[10px] text-muted-foreground mt-0.5 text-right">
                {(form.descripcion || "").length}/800 · aparece en catálogo público, detalle de producto y pedidos
              </div>
            </div>
            <div className="col-span-2 flex justify-end gap-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="producto-save">{editing ? "Guardar cambios" : "Crear producto"}</Button>
            </div>
          </form>

          {/* Historial de precios (solo en edición y si hay cambios) */}
          {editing && historial && historial.total_cambios > 0 && (
            <div className="mt-6 pt-5 border-t border-border" data-testid="historial-precios">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground mb-3">
                Historial de precios · {historial.total_cambios} cambios
              </div>
              <div className="max-h-[220px] overflow-y-auto space-y-1.5">
                {historial.historial.slice().reverse().map((h, idx) => {
                  const label = h.campo === "precio_sugerido" ? "Sugerido" : h.campo === "precio_minimo" ? "Mínimo" : "Costo";
                  const subio = (h.nuevo || 0) > (h.anterior || 0);
                  return (
                    <div key={idx} className="flex items-center gap-3 text-xs bg-muted/40 rounded-md px-3 py-2">
                      <span className="w-16 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
                      <span className="tabular-nums text-muted-foreground line-through">{formatCOP(h.anterior)}</span>
                      <span className={`tabular-nums font-semibold ${subio ? "text-emerald-700" : "text-rose-700"}`}>
                        {subio ? "↑" : "↓"} {formatCOP(h.nuevo)}
                      </span>
                      <span className="text-muted-foreground ml-auto text-[10px]">
                        {new Date(h.at).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "2-digit" })}
                        {h.actor_name && ` · ${h.actor_name}`}
                      </span>
                      {h.motivo && <span className="text-[10px] italic text-muted-foreground/80 truncate max-w-[180px]">"{h.motivo}"</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjust stock modal */}
      <Dialog open={!!adjustTarget} onOpenChange={(v) => { if (adjusting) return; if (!v) setAdjustTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Ajustar stock</DialogTitle>
            <p className="text-sm text-muted-foreground">{adjustTarget?.nombre}</p>
          </DialogHeader>
          <form onSubmit={submitAdjust} className="space-y-4 mt-4">
            <div>
              <Label>Cantidad (positivo suma, negativo resta)</Label>
              <Input type="number" step="1" required value={adjust.cantidad} onChange={(e) => setAdjust({ ...adjust, cantidad: e.target.value })} className="h-11 mt-1" placeholder="Ej: 50 o -10" data-testid="adjust-cantidad" />
            </div>
            <div>
              <Label>Motivo</Label>
              <Input required value={adjust.motivo} onChange={(e) => setAdjust({ ...adjust, motivo: e.target.value })} className="h-11 mt-1" placeholder="Ej: Recibi mercancia, ajuste por inventario fisico..." />
            </div>
            <Button type="submit" disabled={adjusting} className="w-full bg-primary hover:bg-primary/90" data-testid="adjust-submit">{adjusting ? "Aplicando..." : "Aplicar ajuste"}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Wizard de importación de contenedor (2 pasos) */}
      <ContainerImportWizard
        open={containerWizardOpen}
        onOpenChange={setContainerWizardOpen}
        onSuccess={() => load()}
      />

      {/* Importador dedicado del formato heredado Beem (REFERENCIA/NOMBRE/DESCRIPCION/DETAL/MAYORISTA) */}
      <ImportarLegacyBeemModal
        open={legacyBeemOpen}
        onOpenChange={setLegacyBeemOpen}
        onImported={() => load()}
      />

      {/* Modal: Historial de contenedores con opción de eliminar (director) */}
      <Dialog open={histContOpen} onOpenChange={setHistContOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="historial-contenedores-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Contenedores importados</DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Si un contenedor se importó con datos erróneos, puedes eliminarlo aquí. El sistema revertirá el stock que agregó y archivará los productos creados exclusivamente por ese contenedor.
            </p>
          </DialogHeader>
          <div className="mt-4">
            {histContLoading ? (
              <div className="py-16 text-center text-sm text-muted-foreground">Cargando historial...</div>
            ) : histContenedores.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">Aún no hay contenedores importados.</div>
            ) : (
              <div className="space-y-2">
                {histContenedores.map((c) => (
                  <div key={c.id} className="p-3 rounded-lg border border-border bg-card flex items-start justify-between gap-3" data-testid={`hist-contenedor-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-foreground truncate">{c.referencia || "Sin referencia"}</div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                        {c.importado_at && <span>📅 {new Date(c.importado_at).toLocaleString("es-CO")}</span>}
                        {c.creados > 0 && <span>+{c.creados} nuevos</span>}
                        {c.actualizados > 0 && <span>{c.actualizados} actualizados</span>}
                        {c.unidades_recibidas > 0 && <span className="font-mono tabular-nums">{c.unidades_recibidas.toLocaleString("es-CO")} und.</span>}
                      </div>
                      {c.invoice_no && <div className="text-[10px] text-muted-foreground/70 mt-0.5">Invoice: {c.invoice_no}</div>}
                      {c.recosteado_at && <div className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5" data-testid={`hist-contenedor-recosteado-${c.id}`}>Costeo corregido el {new Date(c.recosteado_at).toLocaleString("es-CO")} ({(c.recosteos || []).length} vez/veces)</div>}
                      {/* Jun/2026: parámetros de costeo con los que se importó (para auditar TRM) */}
                      <div className="text-[11px] mt-1 flex flex-wrap gap-x-3 gap-y-0.5" data-testid={`hist-contenedor-costeo-${c.id}`}>
                        <span className="font-semibold text-foreground">TRM aplicada: {c.trm_aplicada ? `$ ${Number(c.trm_aplicada).toLocaleString("es-CO")}` : "—"}</span>
                        {c.pct_gastos_default != null && <span className="text-muted-foreground">Gastos {pctFmt(c.pct_gastos_default)}%</span>}
                        {c.pct_margen_minimo_default != null && <span className="text-muted-foreground">Mín {pctFmt(c.pct_margen_minimo_default)}%</span>}
                        {c.pct_margen_sugerido_default != null && <span className="text-muted-foreground">Sug {pctFmt(c.pct_margen_sugerido_default)}%</span>}
                        {c.gastos_landed_total_cop > 0 && <span className="text-muted-foreground">Landed $ {Number(c.gastos_landed_total_cop).toLocaleString("es-CO")}</span>}
                        <button onClick={() => setHistContAbierto(histContAbierto === c.id ? null : c.id)} className="text-primary font-semibold hover:underline" data-testid={`hist-contenedor-toggle-${c.id}`}>
                          {histContAbierto === c.id ? "Ocultar detalle ▲" : "Ver detalle ▼"}
                        </button>
                        {c.referencia && (
                          <button onClick={() => { setHistContOpen(false); navigate(`/productos?tab=inventario&contenedor=${encodeURIComponent(c.referencia)}`); }} className="text-primary font-semibold hover:underline" title="Ver en Inventario los productos que entraron en este contenedor" data-testid={`hist-contenedor-ver-productos-${c.id}`}>
                            Ver productos →
                          </button>
                        )}
                      </div>
                      {histContAbierto === c.id && (
                        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-[11px] rounded-lg bg-muted/40 p-2.5" data-testid={`hist-contenedor-detalle-${c.id}`}>
                          <ContDato label="Contenedor" value={c.container_no} />
                          <ContDato label="Invoice" value={c.invoice_no} />
                          <ContDato label="BL" value={c.bl_no} />
                          <ContDato label="Naviera" value={c.shipping_line} />
                          <ContDato label="Puerto" value={c.puerto} />
                          <ContDato label="Zarpe (ETD)" value={c.fecha_zarpe} />
                          <ContDato label="Recepción" value={c.fecha_recepcion ? String(c.fecha_recepcion).slice(0, 10) : null} />
                          <ContDato label="CBM" value={c.cbm} />
                          <ContDato label="Peso (kg)" value={c.peso_kg ? Number(c.peso_kg).toLocaleString("es-CO") : null} />
                          <ContDato label="Cartones" value={c.ctns_totales ? Number(c.ctns_totales).toLocaleString("es-CO") : null} />
                          <ContDato label="TRM aplicada" value={c.trm_aplicada ? `$ ${Number(c.trm_aplicada).toLocaleString("es-CO")} / USD` : null} strong />
                          <ContDato label="% Gastos (nacionalización, fletes…)" value={c.pct_gastos_default != null ? `${pctFmt(c.pct_gastos_default)}%` : null} strong />
                          <ContDato label="% Margen mínimo" value={c.pct_margen_minimo_default != null ? `${pctFmt(c.pct_margen_minimo_default)}%` : null} />
                          <ContDato label="% Margen sugerido" value={c.pct_margen_sugerido_default != null ? `${pctFmt(c.pct_margen_sugerido_default)}%` : null} />
                          <ContDato label="Gastos landed (COP)" value={c.gastos_landed_total_cop > 0 ? `$ ${Number(c.gastos_landed_total_cop).toLocaleString("es-CO")}` : "No se usó"} />
                          <ContDato label="Prorrateo" value={c.metodo_prorrateo === "unidades" ? "Por unidades" : c.metodo_prorrateo === "valor" ? "Por valor FOB" : c.metodo_prorrateo} />
                          <ContDato label="Modo de costo" value={c.modo_costo} />
                          <ContDato label="Productos nuevos" value={c.creados} />
                          <ContDato label="Actualizados (solo stock)" value={c.actualizados_solo_stock} />
                          <ContDato label="Actualizados (con precio)" value={c.actualizados_con_precio} />
                          <ContDato label="SKUs con lote" value={c.skus_lote} />
                          <ContDato label="Unidades recibidas" value={c.unidades_recibidas ? Number(c.unidades_recibidas).toLocaleString("es-CO") : null} />
                          <ContDato label="Unidades aún disponibles" value={c.unidades_disponibles != null ? Number(c.unidades_disponibles).toLocaleString("es-CO") : null} />
                          <ContDato label="Valor del lote al costo" value={c.valor_lote_cop ? `$ ${Number(c.valor_lote_cop).toLocaleString("es-CO")}` : null} strong />
                          <ContDato label="Importado por" value={c.importado_por_nombre} />
                          <ContDato label="Fecha de importación" value={c.importado_at ? new Date(c.importado_at).toLocaleString("es-CO") : null} />
                          <ContDato label="Excel origen" value={c.excel_size_bytes ? `${(c.excel_size_bytes / 1024 / 1024).toFixed(1)} MB` : null} />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setRecostearCont(c)}
                        className="h-8 text-xs"
                        data-testid={`recostear-contenedor-${c.id}`}
                        title="Corregir TRM o % de gastos y recalcular el costo de los lotes"
                      >
                        Corregir costeo
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDesgloseCont(c)}
                        className="h-8 text-xs border-violet-300 text-violet-800 hover:bg-violet-50"
                        data-testid={`desglosar-contenedor-${c.id}`}
                        title="Detectar productos cargados con costo por paquete y convertirlos a costo por unidad"
                      >
                        Desglosar empaques
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConciliarCont(c)}
                        className="h-8 text-xs"
                        data-testid={`conciliar-contenedor-${c.id}`}
                        title="Comparar las cantidades de los lotes contra el Excel original y corregir diferencias"
                      >
                        Conciliar con Excel
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFusionesCont(c)}
                        className="h-8 text-xs"
                        data-testid={`fusiones-contenedor-${c.id}`}
                        title="Ver referencias que el importador sumó a productos existentes y separarlas"
                      >
                        Fusiones
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => eliminarContenedor(c)}
                        disabled={histContEliminando === c.id}
                        className="h-8 text-xs text-rose-700 border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                        data-testid={`eliminar-contenedor-${c.id}`}
                      >
                        {histContEliminando === c.id ? "Eliminando..." : "Eliminar"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <RecostearContenedorDialog
        contenedor={recostearCont}
        open={!!recostearCont}
        onClose={() => setRecostearCont(null)}
        onDone={() => { cargarHistContenedores(); load(); }}
      />

      <DesglosarEmpaqueDialog
        open={bulkDesglosarOpen}
        onClose={() => setBulkDesglosarOpen(false)}
        productoIds={Array.from(selected)}
        onDone={() => { setSelected(new Set()); load(); }}
      />

      <DesgloseContenedorDialog
        contenedor={desgloseCont}
        open={!!desgloseCont}
        onClose={() => setDesgloseCont(null)}
        onDone={() => { cargarHistContenedores(); load(); }}
      />

      <ConciliarContenedorDialog
        contenedor={conciliarCont}
        open={!!conciliarCont}
        onClose={() => setConciliarCont(null)}
        onDone={() => { cargarHistContenedores(); load(); }}
      />

      <FusionesDialog
        contenedor={fusionesCont}
        open={!!fusionesCont}
        onClose={() => setFusionesCont(null)}
        onDone={() => { cargarHistContenedores(); load(); }}
      />

      {/* Modal: Agrupar variantes (heurística + confirmación inline) */}
      <Dialog open={agruparOpen} onOpenChange={setAgruparOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agrupar-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-primary tracking-wider">Agrupar variantes</div>
                <DialogTitle className="font-display text-2xl mt-1">Productos que parecen variantes del mismo modelo</DialogTitle>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Detectamos productos con el mismo nombre base. Elige uno como <b>padre</b> y marca los que quedan
              como colores/variantes. Después, en el armador de pedidos aparecerá un solo ítem con selector de
              colores + opción <b>Surtido</b> (bodega decide).
            </p>
          </DialogHeader>

          {agruparLoading ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Analizando catálogo...</div>
          ) : agruparGrupos.length === 0 ? (
            <div className="py-10 text-center">
              <Layers className="w-8 h-8 mx-auto text-muted-foreground/40" />
              <p className="mt-3 text-sm text-muted-foreground">No encontramos grupos automáticos.</p>
              <p className="mt-1 text-xs text-muted-foreground/70">
                Tip: los productos deben tener el mismo nombre base para ser detectados.
              </p>
            </div>
          ) : (
            <div className="space-y-4 mt-2">
              {agruparGrupos.map((g, idx) => {
                const sel = agruparSel[idx] || { ids: new Set(), padre_id: "" };
                const toggle = (id) => {
                  if (id === sel.padre_id) return; // padre no se toggle
                  const ns = new Set(sel.ids);
                  if (ns.has(id)) ns.delete(id); else ns.add(id);
                  setAgruparSel({ ...agruparSel, [idx]: { ...sel, ids: ns } });
                };
                const setPadreId = (padre_id) => {
                  // al cambiar el padre, sacamos ese id de la selección de variantes
                  const ns = new Set(sel.ids);
                  ns.delete(padre_id);
                  setAgruparSel({ ...agruparSel, [idx]: { padre_id, ids: ns } });
                };
                const padreProd = g.productos.find((p) => p.id === sel.padre_id);
                return (
                  <div key={idx} className="border border-border rounded-xl p-4 bg-card" data-testid={`agrupar-grupo-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-foreground">
                        {g.sugerido_padre}
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary uppercase font-bold">
                        {g.conteo} similares
                      </span>
                    </div>
                    <div className="mb-3">
                      <Label className="text-[11px] font-bold uppercase">Producto padre (representa el modelo base)</Label>
                      <select
                        value={sel.padre_id}
                        onChange={(e) => setPadreId(e.target.value)}
                        className="w-full h-10 mt-1 border border-border rounded-md px-3 bg-card text-sm"
                        data-testid={`agrupar-padre-${idx}`}
                      >
                        <option value="">Elige un producto...</option>
                        {g.productos.map((p) => (
                          <option key={p.id} value={p.id}>{p.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div className="text-[11px] text-muted-foreground uppercase font-bold mb-2">
                      {sel.ids.size} variantes seleccionadas · Categoría: {padreProd?.categoria || "—"}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                      {g.productos.map((p) => {
                        const isPadre = sel.padre_id === p.id;
                        const checked = sel.ids.has(p.id);
                        return (
                          <label key={p.id} className={`flex items-start gap-2 p-2 border rounded-lg transition-colors ${isPadre ? "border-primary bg-primary/10 ring-2 ring-primary/30 cursor-default" : checked ? "border-primary/40 bg-primary/5 cursor-pointer" : "border-border hover:bg-muted/40 cursor-pointer"}`}>
                            <input
                              type="checkbox"
                              checked={isPadre || checked}
                              onChange={() => toggle(p.id)}
                              disabled={isPadre}
                              className="mt-0.5"
                              data-testid={`agrupar-check-${p.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-foreground truncate">{p.nombre}</div>
                              <div className="text-[11px] text-muted-foreground truncate">
                                {p.variante_label || "—"} · {p.codigo || p.sku || "—"} · stock {p.stock_actual} · {formatCOP(p.precio_sugerido)}
                              </div>
                              {isPadre && <div className="text-[10px] text-primary font-bold mt-0.5">Este es el padre</div>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setAgruparGrupos(agruparGrupos.filter((_, i) => i !== idx)); }}
                        data-testid={`agrupar-descartar-${idx}`}
                      >
                        Descartar sugerencia
                      </Button>
                      <Button
                        size="sm"
                        disabled={agruparSaving || !sel.padre_id || sel.ids.size < 1}
                        onClick={async () => {
                          setAgruparSaving(true);
                          try {
                            await api.post("/productos/_/confirmar-grupo-variantes", {
                              padre_id: sel.padre_id,
                              variante_ids: Array.from(sel.ids),
                            });
                            toast.success(`Agrupadas ${sel.ids.size} variantes bajo "${padreProd?.nombre || 'el padre'}"`);
                            setAgruparGrupos(agruparGrupos.filter((_, i) => i !== idx));
                            load();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "No se pudo agrupar");
                          } finally { setAgruparSaving(false); }
                        }}
                        className="bg-primary hover:bg-primary/90"
                        data-testid={`agrupar-confirmar-${idx}`}
                      >
                        {agruparSaving ? "Agrupando..." : `Agrupar ${sel.ids.size} variantes`}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setAgruparOpen(false)}>Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox global para ver imagenes de productos en grande */}
      <ImageLightbox
        src={lightbox?.src}
        alt={lightbox?.alt}
        open={!!lightbox}
        onOpenChange={(v) => !v && setLightbox(null)}
      />

      {/* Modal de detalle de familia: grid de colores con foto, stock y precio */}
      <FamilyDetailModal
        familia={familiaDetalle}
        onClose={() => setFamiliaDetalle(null)}
        onAjustar={(v) => { setFamiliaRetornoSnap(familiaDetalle); setAdjustTarget(v); setAdjust({ cantidad: "", motivo: "" }); setFamiliaDetalle(null); }}
        onEditar={(v) => { setFamiliaRetornoSnap(familiaDetalle); startEdit(v); setFamiliaDetalle(null); }}
        canAdjust={canAdjustStock}
        canEdit={canEdit}
        canArchive={canArchive}
        isDirector={isDirector}
        canBulk={canBulk}
        reglas={reglas}
        onFamiliaCambio={async () => {
          // Oct/2026 — bug fix: cuando se edita algo DESDE el modal familiar
          // (p. ej. inline label edit, mover, fusión), el callback solo hacía
          // load() pero el modal seguía mostrando datos viejos hasta cerrar
          // y reabrir. Ahora recargamos Y reconstruimos la familia visible
          // en pantalla con los datos frescos.
          await load();
          if (familiaDetalle) {
            const snap = { familiaKey: familiaDetalle.familiaKey, familiaNombre: familiaDetalle.familiaNombre };
            // Delay corto para dejar que React propague setItems → itemsRef
            setTimeout(() => {
              const fresh = rebuildFamilia(snap);
              if (fresh) setFamiliaDetalle(fresh);
            }, 120);
          }
        }}
        onBulkFromFamily={(kind, ids) => {
          // Setea selección en el padre y abre el modal correcto. Dejamos el
          // modal de familia abierto para que al cerrar bulk siga viéndose.
          setSelected(new Set(ids));
          if (kind === "precios") {
            setBulkPrecios({
              modo: "fijo", aplicar_sugerido: true, aplicar_minimo: true,
              precio_sugerido_valor: "", precio_minimo_valor: "", porcentaje: "", motivo: "",
            });
            setBulkPreciosOpen(true);
          } else if (kind === "codigo") {
            setBulkCodigoValor(""); setBulkCodigoOpen(true);
          } else if (kind === "empaque") {
            setBulkEmpaqueValor("unidad"); setBulkEmpaqueOpen(true);
          }
        }}
        onAplicarReglaFromFamily={async (reglaId, ids) => {
          try {
            const { data } = await api.post("/productos/_/asignar-regla", {
              producto_ids: ids,
              regla_comision_id: reglaId,
            });
            const nombreRegla = reglas.find((r) => r.id === reglaId)?.nombre || "regla";
            toast.success(`${data.afectados} producto(s) asignados a "${nombreRegla}"`);
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo asignar");
          }
        }}
      />

      {/* Modal de confirmación: eliminar TODA una familia (grupo completo) */}
      <Dialog open={!!eliminarFamiliaTarget} onOpenChange={(v) => { if (!v) { setEliminarFamiliaTarget(null); setEliminarFamiliaMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="eliminar-familia-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Trash2 className="w-5 h-5" />
              Eliminar grupo completo
            </DialogTitle>
          </DialogHeader>
          {eliminarFamiliaTarget && (
            <div className="space-y-4 pt-2">
              <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-4 py-3 text-sm">
                <div className="font-semibold text-rose-800">"{eliminarFamiliaTarget.familiaNombre}"</div>
                <div className="text-xs text-rose-700/80 mt-1">
                  Se archivarán <b>{eliminarFamiliaTarget.count}</b> {eliminarFamiliaTarget.count === 1 ? "color/ítem" : "colores/ítems"} de este grupo.
                  Los pedidos anteriores no se ven afectados y puedes reactivar cada producto desde "Archivados".
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Motivo (opcional pero recomendado)</label>
                <textarea
                  value={eliminarFamiliaMotivo}
                  onChange={(e) => setEliminarFamiliaMotivo(e.target.value)}
                  placeholder="Ej: producto descontinuado, error de catálogo, mercancía dañada..."
                  className="w-full min-h-[80px] rounded-md border border-border px-3 py-2 text-sm resize-none"
                  data-testid="eliminar-familia-motivo"
                />
                <div className="text-[10px] text-muted-foreground">Se registra en el histórico de movimientos de cada producto.</div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setEliminarFamiliaTarget(null); setEliminarFamiliaMotivo(""); }} disabled={eliminandoFamilia} data-testid="eliminar-familia-cancelar">
                  Cancelar
                </Button>
                <Button
                  onClick={eliminarFamiliaCompleta}
                  disabled={eliminandoFamilia}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="eliminar-familia-confirmar"
                >
                  {eliminandoFamilia ? "Eliminando..." : `Eliminar grupo y ${eliminarFamiliaTarget.count} ${eliminarFamiliaTarget.count === 1 ? "ítem" : "ítems"}`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Editor MASIVO de precios (Feb/2026) */}
      <Dialog open={bulkPreciosOpen} onOpenChange={(v) => { if (!bulkPreciosSaving) setBulkPreciosOpen(v); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="bulk-precios-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-emerald-700 tracking-wider">Edición masiva</div>
                <DialogTitle className="font-display text-2xl mt-1">
                  Precios de {selected.size} producto{selected.size === 1 ? "" : "s"}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-5 py-2">
            {/* Selector de modo */}
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Modo de ajuste</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {[
                  { v: "fijo", label: "Precio fijo", desc: "Reemplazar por un valor exacto" },
                  { v: "absoluto", label: "Sumar / restar $", desc: "Ajuste absoluto sobre el actual" },
                  { v: "porcentual", label: "Porcentaje %", desc: "Aumentar/disminuir X% del actual" },
                ].map((m) => (
                  <button
                    key={m.v}
                    type="button"
                    onClick={() => setBulkPrecios({ ...bulkPrecios, modo: m.v })}
                    data-testid={`bulk-precios-modo-${m.v}`}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      bulkPrecios.modo === m.v
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-border hover:border-emerald-300 bg-card"
                    }`}
                  >
                    <div className={`text-sm font-bold ${bulkPrecios.modo === m.v ? "text-emerald-800" : "text-foreground"}`}>{m.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{m.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Que precios aplicar */}
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Aplicar a</Label>
              <div className="flex items-center gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={bulkPrecios.aplicar_sugerido}
                    onChange={(e) => setBulkPrecios({ ...bulkPrecios, aplicar_sugerido: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="bulk-precios-toggle-sugerido"
                  />
                  <span className="font-medium">Precio sugerido</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={bulkPrecios.aplicar_minimo}
                    onChange={(e) => setBulkPrecios({ ...bulkPrecios, aplicar_minimo: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="bulk-precios-toggle-minimo"
                  />
                  <span className="font-medium">Precio mínimo</span>
                </label>
              </div>
            </div>

            {/* Inputs de valor segun modo */}
            {bulkPrecios.modo === "porcentual" ? (
              <div>
                <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">
                  Porcentaje (ej. 10 = +10%, -5 = -5%)
                </Label>
                <div className="relative mt-1.5 max-w-xs">
                  <Input
                    type="number"
                    step="0.5"
                    value={bulkPrecios.porcentaje}
                    onChange={(e) => setBulkPrecios({ ...bulkPrecios, porcentaje: e.target.value })}
                    placeholder="10"
                    className="pr-10 text-lg font-semibold"
                    data-testid="bulk-precios-porcentaje"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">%</div>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Se aplica el mismo % a los precios activados arriba.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {bulkPrecios.aplicar_sugerido && (
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">
                      {bulkPrecios.modo === "fijo" ? "Nuevo precio sugerido" : "Sumar al sugerido (+/-)"}
                    </Label>
                    {bulkPrecios.modo === "fijo" ? (
                      <MoneyInput
                        value={bulkPrecios.precio_sugerido_valor}
                        onValueChange={(v) => setBulkPrecios({ ...bulkPrecios, precio_sugerido_valor: v })}
                        placeholder="$ 25.000"
                        className="mt-1.5"
                        data-testid="bulk-precios-sugerido-valor"
                      />
                    ) : (
                      <Input
                        type="number"
                        step="500"
                        value={bulkPrecios.precio_sugerido_valor}
                        onChange={(e) => setBulkPrecios({ ...bulkPrecios, precio_sugerido_valor: e.target.value })}
                        placeholder="2000 o -1500"
                        className="mt-1.5 tabular-nums"
                        data-testid="bulk-precios-sugerido-valor"
                      />
                    )}
                  </div>
                )}
                {bulkPrecios.aplicar_minimo && (
                  <div>
                    <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">
                      {bulkPrecios.modo === "fijo" ? "Nuevo precio mínimo" : "Sumar al mínimo (+/-)"}
                    </Label>
                    {bulkPrecios.modo === "fijo" ? (
                      <MoneyInput
                        value={bulkPrecios.precio_minimo_valor}
                        onValueChange={(v) => setBulkPrecios({ ...bulkPrecios, precio_minimo_valor: v })}
                        placeholder="$ 20.000"
                        className="mt-1.5"
                        data-testid="bulk-precios-minimo-valor"
                      />
                    ) : (
                      <Input
                        type="number"
                        step="500"
                        value={bulkPrecios.precio_minimo_valor}
                        onChange={(e) => setBulkPrecios({ ...bulkPrecios, precio_minimo_valor: e.target.value })}
                        placeholder="2000 o -1500"
                        className="mt-1.5 tabular-nums"
                        data-testid="bulk-precios-minimo-valor"
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Motivo */}
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">Motivo (opcional)</Label>
              <Input
                value={bulkPrecios.motivo}
                onChange={(e) => setBulkPrecios({ ...bulkPrecios, motivo: e.target.value })}
                placeholder="Ej: Ajuste inflación febrero 2026"
                className="mt-1.5"
                data-testid="bulk-precios-motivo"
              />
            </div>

            {/* Vista previa */}
            {selected.size > 0 && (
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-muted/40 px-4 py-2 text-xs font-bold uppercase text-muted-foreground tracking-wider flex items-center justify-between">
                  <span>Vista previa (primeros 8)</span>
                  <span className="tabular-nums normal-case font-medium">{selected.size} producto{selected.size === 1 ? "" : "s"}</span>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-card sticky top-0 border-b border-border">
                      <tr className="text-left text-muted-foreground">
                        <th className="px-3 py-2 font-semibold">Producto</th>
                        <th className="px-3 py-2 font-semibold text-right">Sugerido antes → después</th>
                        <th className="px-3 py-2 font-semibold text-right">Mínimo antes → después</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.filter((p) => selected.has(p.id)).slice(0, 8).map((p) => {
                        const calc = (actual, aplica, tipo) => {
                          if (!aplica) return actual;
                          const a = parseInt(actual || 0, 10);
                          if (bulkPrecios.modo === "fijo") {
                            const v = tipo === "sug" ? bulkPrecios.precio_sugerido_valor : bulkPrecios.precio_minimo_valor;
                            return Math.max(0, parseInt(v || 0, 10));
                          }
                          if (bulkPrecios.modo === "absoluto") {
                            const v = tipo === "sug" ? bulkPrecios.precio_sugerido_valor : bulkPrecios.precio_minimo_valor;
                            return Math.max(0, a + parseInt(v || 0, 10));
                          }
                          if (bulkPrecios.modo === "porcentual") {
                            const pct = parseFloat(bulkPrecios.porcentaje || 0);
                            return Math.max(0, Math.round(a * (1 + pct / 100)));
                          }
                          return actual;
                        };
                        const sugAntes = p.precio_sugerido || p.precio_base || 0;
                        const minAntes = p.precio_minimo || 0;
                        let sugDespues = calc(sugAntes, bulkPrecios.aplicar_sugerido, "sug");
                        let minDespues = calc(minAntes, bulkPrecios.aplicar_minimo, "min");
                        // Coherencia visual
                        if (bulkPrecios.aplicar_sugerido && bulkPrecios.aplicar_minimo && minDespues > sugDespues) minDespues = sugDespues;
                        else if (bulkPrecios.aplicar_sugerido && !bulkPrecios.aplicar_minimo && minAntes > sugDespues) minDespues = sugDespues;
                        else if (!bulkPrecios.aplicar_sugerido && bulkPrecios.aplicar_minimo && minDespues > sugAntes && sugAntes > 0) minDespues = sugAntes;
                        const sugCambia = sugDespues !== sugAntes;
                        const minCambia = minDespues !== minAntes;
                        return (
                          <tr key={p.id} className="border-b border-border/40">
                            <td className="px-3 py-2 truncate max-w-[240px]" title={p.nombre}>
                              <div>{p.nombre}</div>
                              {(p.codigo || p.sku) && (
                                <div className="text-[10px] font-mono text-indigo-700">{p.codigo || p.sku}</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              <span className="text-muted-foreground">{formatCOP(sugAntes)}</span>
                              {sugCambia && (
                                <>
                                  <span className="mx-1 text-muted-foreground">→</span>
                                  <span className={`font-bold ${sugDespues > sugAntes ? "text-emerald-700" : "text-rose-700"}`}>{formatCOP(sugDespues)}</span>
                                </>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              <span className="text-muted-foreground">{formatCOP(minAntes)}</span>
                              {minCambia && (
                                <>
                                  <span className="mx-1 text-muted-foreground">→</span>
                                  <span className={`font-bold ${minDespues > minAntes ? "text-emerald-700" : "text-rose-700"}`}>{formatCOP(minDespues)}</span>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {selected.size > 8 && (
                  <div className="bg-muted/20 px-4 py-2 text-xs text-muted-foreground text-center">
                    ... y {selected.size - 8} más
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center gap-3 pt-3 border-t border-border">
            <div className="text-xs text-muted-foreground">
              Se aplicará a <b className="text-foreground">{selected.size}</b> producto{selected.size === 1 ? "" : "s"} seleccionado{selected.size === 1 ? "" : "s"}.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkPreciosOpen(false)} disabled={bulkPreciosSaving} data-testid="bulk-precios-cancel">
                Cancelar
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={
                  bulkPreciosSaving ||
                  selected.size === 0 ||
                  (!bulkPrecios.aplicar_sugerido && !bulkPrecios.aplicar_minimo) ||
                  (bulkPrecios.modo === "porcentual" && (bulkPrecios.porcentaje === "" || bulkPrecios.porcentaje === null)) ||
                  (bulkPrecios.modo !== "porcentual" && bulkPrecios.aplicar_sugerido && (bulkPrecios.precio_sugerido_valor === "" || bulkPrecios.precio_sugerido_valor === null)) ||
                  (bulkPrecios.modo !== "porcentual" && bulkPrecios.aplicar_minimo && (bulkPrecios.precio_minimo_valor === "" || bulkPrecios.precio_minimo_valor === null))
                }
                onClick={async () => {
                  setBulkPreciosSaving(true);
                  try {
                    const payload = {
                      producto_ids: Array.from(selected),
                      modo: bulkPrecios.modo,
                      aplicar_sugerido: bulkPrecios.aplicar_sugerido,
                      aplicar_minimo: bulkPrecios.aplicar_minimo,
                      motivo: bulkPrecios.motivo || null,
                    };
                    if (bulkPrecios.modo === "porcentual") {
                      payload.porcentaje = parseFloat(bulkPrecios.porcentaje);
                    } else {
                      if (bulkPrecios.aplicar_sugerido) payload.precio_sugerido_valor = parseFloat(bulkPrecios.precio_sugerido_valor);
                      if (bulkPrecios.aplicar_minimo) payload.precio_minimo_valor = parseFloat(bulkPrecios.precio_minimo_valor);
                    }
                    const { data } = await api.post("/productos/_/bulk-precios", payload);
                    toast.success(`${data.afectados} producto(s) actualizados`);
                    setBulkPreciosOpen(false);
                    setSelected(new Set());
                    load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "No se pudo actualizar precios");
                  } finally {
                    setBulkPreciosSaving(false);
                  }
                }}
                data-testid="bulk-precios-confirm"
              >
                {bulkPreciosSaving ? "Aplicando..." : "Confirmar cambios"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Código masivo (Feb/2026) — aplica el MISMO código real a todos
          los productos seleccionados. Útil cuando varios colores comparten
          caja física con la misma referencia del proveedor.
          Feb/2026 (v2): ahora muestra PREVIEW de cada producto a cambiar
          (foto, nombre, color, código actual → código nuevo) para que no
          sea "a ciegas". */}
      <Dialog open={bulkCodigoOpen} onOpenChange={(v) => { if (!bulkCodigoSaving) setBulkCodigoOpen(v); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="bulk-codigo-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                <Wand2 className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-indigo-700 tracking-wider">Código real masivo</div>
                <DialogTitle className="font-display text-xl mt-1">
                  Asignar código a {selected.size} producto{selected.size === 1 ? "" : "s"}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-2 flex-1 overflow-hidden flex flex-col">
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">
                Código real (CLIENT'S REF)
              </Label>
              <Input
                value={bulkCodigoValor}
                onChange={(e) => setBulkCodigoValor(e.target.value.toUpperCase())}
                placeholder="Ej: EF26-148"
                className="mt-1.5 font-mono text-lg"
                autoFocus
                data-testid="bulk-codigo-valor"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Se aplicará exactamente este valor a los {selected.size} productos.
                Sincroniza también los lotes de inventario.
              </p>
            </div>
            {/* Preview de los productos que se afectarán */}
            <div className="flex-1 overflow-hidden flex flex-col border border-border rounded-lg">
              <div className="px-3 py-2 bg-muted/40 border-b border-border text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Preview — productos a cambiar</span>
                <span className="text-indigo-700 tabular-nums">{selected.size} ítem{selected.size === 1 ? "" : "s"}</span>
              </div>
              <div className="overflow-y-auto flex-1 max-h-[320px] divide-y divide-border/60" data-testid="bulk-codigo-preview">
                {(() => {
                  const porId = new Map(items.map((p) => [p.id, p]));
                  const previewItems = Array.from(selected).map((id) => porId.get(id)).filter(Boolean);
                  if (previewItems.length === 0) {
                    return <div className="px-4 py-8 text-center text-sm text-muted-foreground">No hay productos seleccionados</div>;
                  }
                  const nuevoCod = bulkCodigoValor.trim().toUpperCase();
                  return previewItems.map((p) => {
                    const color = p.color || p.nombre?.split(" - ")[1]?.trim() || "";
                    const codActual = p.codigo || "";
                    const sinCambio = nuevoCod && codActual && codActual === nuevoCod;
                    return (
                      <div key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30" data-testid={`bulk-codigo-preview-${p.id}`}>
                        {p.imagen_path ? (
                          <img src={`${API}/catalogo/img/${p.id}?w=80&v=${p.imagen_actualizada_at || 0}`} alt="" loading="lazy" className="w-9 h-9 rounded-md object-cover border border-border flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-md border border-dashed border-border/60 flex items-center justify-center text-muted-foreground/40 flex-shrink-0">
                            <ImageIcon className="w-4 h-4" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate" title={p.nombre}>{p.nombre}</div>
                          {color && <div className="text-[11px] text-muted-foreground truncate">Color: {color}</div>}
                        </div>
                        <div className="flex items-center gap-1 text-[11px] font-mono flex-shrink-0">
                          <span className={codActual ? "text-muted-foreground line-through" : "text-muted-foreground/50 italic"} title="Código actual">
                            {codActual || "(sin código)"}
                          </span>
                          <ArrowRight className="w-3 h-3 text-indigo-500" />
                          <span className={`font-bold px-1.5 py-0.5 rounded ${nuevoCod ? (sinCambio ? "bg-muted text-muted-foreground" : "bg-indigo-100 text-indigo-800") : "bg-amber-100 text-amber-800"}`} title="Código nuevo">
                            {nuevoCod || "???"}
                          </span>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setBulkCodigoOpen(false)} disabled={bulkCodigoSaving} data-testid="bulk-codigo-cancel">
              Cancelar
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              disabled={bulkCodigoSaving || !bulkCodigoValor.trim() || selected.size === 0}
              onClick={async () => {
                const cod = bulkCodigoValor.trim().toUpperCase();
                if (!cod) return;
                setBulkCodigoSaving(true);
                try {
                  const items = Array.from(selected).map((producto_id) => ({ producto_id, codigo: cod }));
                  const { data } = await api.post("/productos/_/bulk-codigos", { items, motivo: "Código masivo manual" });
                  toast.success(`${data.afectados} producto(s) actualizados con ${cod}`);
                  setBulkCodigoOpen(false);
                  setSelected(new Set());
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "No se pudo asignar código");
                } finally { setBulkCodigoSaving(false); }
              }}
              data-testid="bulk-codigo-confirm"
            >
              {bulkCodigoSaving ? "Aplicando..." : `Confirmar ${selected.size} cambio${selected.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Empaque masivo (Feb/2026) — asigna la misma unidad de empaque
          (unidad / paquete / cajita / rollo / docena / yarda / metro) a todos
          los productos seleccionados. */}
      <Dialog open={bulkEmpaqueOpen} onOpenChange={(v) => { if (!bulkEmpaqueSaving) setBulkEmpaqueOpen(v); }}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col" data-testid="bulk-empaque-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                <Package className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-amber-700 tracking-wider">Unidad de empaque masiva</div>
                <DialogTitle className="font-display text-xl mt-1">
                  Asignar empaque a {selected.size} producto{selected.size === 1 ? "" : "s"}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-2 flex-1 overflow-hidden flex flex-col">
            <div>
              <Label className="text-xs font-bold uppercase text-muted-foreground tracking-wider">
                Unidad de empaque
              </Label>
              <select
                value={bulkEmpaqueValor}
                onChange={(e) => setBulkEmpaqueValor(e.target.value)}
                className="w-full h-11 mt-1.5 border border-border rounded-md px-3 bg-card text-sm"
                data-testid="bulk-empaque-valor"
              >
                {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-2">
                Reemplaza el valor del campo <strong>unidad</strong> de los {selected.size} productos seleccionados.
                No modifica stock ni precios.
              </p>
            </div>
            {/* Preview breve */}
            <div className="flex-1 overflow-hidden flex flex-col border border-border rounded-lg">
              <div className="px-3 py-2 bg-muted/40 border-b border-border text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center justify-between">
                <span>Preview</span>
                <span className="text-amber-700 tabular-nums">{selected.size} ítem{selected.size === 1 ? "" : "s"}</span>
              </div>
              <div className="overflow-y-auto flex-1 max-h-[260px] divide-y divide-border/60" data-testid="bulk-empaque-preview">
                {(() => {
                  const porId = new Map(items.map((p) => [p.id, p]));
                  const previewItems = Array.from(selected).map((id) => porId.get(id)).filter(Boolean);
                  if (previewItems.length === 0) {
                    return <div className="px-4 py-8 text-center text-sm text-muted-foreground">No hay productos seleccionados</div>;
                  }
                  return previewItems.map((p) => (
                    <div key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30" data-testid={`bulk-empaque-preview-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate" title={p.nombre}>{p.nombre}</div>
                      </div>
                      <div className="flex items-center gap-1 text-[11px] flex-shrink-0">
                        <span className="text-muted-foreground line-through">{p.unidad || "unidad"}</span>
                        <ArrowRight className="w-3 h-3 text-amber-500" />
                        <span className="font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{bulkEmpaqueValor}</span>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => setBulkEmpaqueOpen(false)} disabled={bulkEmpaqueSaving} data-testid="bulk-empaque-cancel">
              Cancelar
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={bulkEmpaqueSaving || !bulkEmpaqueValor || selected.size === 0}
              onClick={async () => {
                setBulkEmpaqueSaving(true);
                try {
                  const { data } = await api.post("/productos/_/bulk-unidad", {
                    producto_ids: Array.from(selected),
                    unidad: bulkEmpaqueValor,
                  });
                  toast.success(`${data.afectados} producto(s) ahora empacan por ${bulkEmpaqueValor}`);
                  setBulkEmpaqueOpen(false);
                  setSelected(new Set());
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "No se pudo asignar empaque");
                } finally { setBulkEmpaqueSaving(false); }
              }}
              data-testid="bulk-empaque-confirm"
            >
              {bulkEmpaqueSaving ? "Aplicando..." : `Confirmar ${selected.size} cambio${selected.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Jerarquía de variantes (padre → sub-padre → color) */}
      <Dialog open={jerarquiaOpen} onOpenChange={setJerarquiaOpen}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="jerarquia-modal">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Layers className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase text-primary tracking-wider">Jerarquía sugerida</div>
                <DialogTitle className="font-display text-2xl mt-1">Grupos con sub-tamaños y colores detectados</DialogTitle>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              La IA detecta 2 niveles: <b>modelo raíz</b> (ej: "Cinta") → <b>sub-modelo</b> (ej: "7mm", "5cm") → <b>color</b> (ej: "Amarillo").
              Al confirmar cada grupo, los productos se agrupan automáticamente y sus nombres se limpian a solo la diferencia (ej: "Cinta 7mm Amarillo" → "Amarillo").
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 mt-2">
            {jerarquiaLoading ? (
              <div className="py-16 text-center text-sm text-muted-foreground">Detectando jerarquías...</div>
            ) : jerarquiaGrupos.length === 0 ? (
              <div className="py-16 text-center">
                <Layers className="w-10 h-10 mx-auto text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">No encontramos grupos jerárquicos.</p>
              </div>
            ) : (
              jerarquiaGrupos.map((g, gi) => (
                <div key={gi} className="border border-border rounded-xl p-4 bg-card" data-testid={`jer-grupo-${gi}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Input
                        value={g.sugerido_padre_nombre}
                        onChange={(e) => setJerarquiaGrupos((gs) => gs.map((x, i) => i === gi ? { ...x, sugerido_padre_nombre: e.target.value } : x))}
                        className="h-9 text-base font-semibold w-72"
                        data-testid={`jer-padre-nombre-${gi}`}
                      />
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary uppercase font-bold">
                        {g.conteo_total} productos · {g.subgrupos?.length || 0} sub-modelos
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setJerarquiaGrupos((gs) => gs.filter((_, i) => i !== gi))}
                        data-testid={`jer-descartar-${gi}`}
                      >
                        Descartar
                      </Button>
                      <Button
                        size="sm"
                        disabled={jerarquiaSaving || ((!g.subgrupos || g.subgrupos.length === 0) && (!g.variantes_directas || g.variantes_directas.length === 0))}
                        onClick={async () => {
                          setJerarquiaSaving(true);
                          try {
                            await api.post("/productos/_/agrupar-jerarquia", {
                              nombre_padre: g.sugerido_padre_nombre,
                              categoria: g.categoria,
                              unidad: g.unidad,
                              subgrupos: (g.subgrupos || []).map((sg) => ({
                                nombre_sub_padre: sg.sugerido_nombre_sub_padre,
                                variantes: sg.variantes.map((v) => ({ id: v.id, nombre_limpio: v.nombre_limpio })),
                              })),
                              variantes_directas: (g.variantes_directas || []).map((v) => ({ id: v.id, nombre_limpio: v.nombre_limpio })),
                            });
                            const totalV = (g.subgrupos || []).reduce((s, sg) => s + sg.variantes.length, 0) + (g.variantes_directas?.length || 0);
                            toast.success(`Jerarquía creada: "${g.sugerido_padre_nombre}" con ${g.subgrupos?.length || 0} sub-modelos y ${totalV} variantes`);
                            setJerarquiaGrupos((gs) => gs.filter((_, i) => i !== gi));
                            load();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "No se pudo aplicar la jerarquía");
                          } finally { setJerarquiaSaving(false); }
                        }}
                        className="bg-primary hover:bg-primary/90"
                        data-testid={`jer-aplicar-${gi}`}
                      >
                        {jerarquiaSaving ? "Aplicando..." : "Aplicar jerarquía"}
                      </Button>
                    </div>
                  </div>

                  {(g.subgrupos || []).map((sg, sgi) => (
                    <div key={sgi} className="ml-4 border-l-2 border-primary/30 pl-4 py-2 mb-3" data-testid={`jer-sub-${gi}-${sgi}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <ChevronRight className="w-4 h-4 text-primary" />
                        <Input
                          value={sg.sugerido_nombre_sub_padre}
                          onChange={(e) => setJerarquiaGrupos((gs) => gs.map((x, i) => i === gi ? { ...x, subgrupos: x.subgrupos.map((y, j) => j === sgi ? { ...y, sugerido_nombre_sub_padre: e.target.value } : y) } : x))}
                          className="h-8 text-sm font-medium w-60"
                          data-testid={`jer-sub-nombre-${gi}-${sgi}`}
                        />
                        <span className="text-[10px] text-muted-foreground">{sg.variantes.length} colores</span>
                        <button
                          type="button"
                          className="ml-auto text-[11px] text-rose-600 hover:underline"
                          onClick={() => setJerarquiaGrupos((gs) => gs.map((x, i) => i === gi ? { ...x, subgrupos: x.subgrupos.filter((_, j) => j !== sgi) } : x))}
                          data-testid={`jer-quitar-sub-${gi}-${sgi}`}
                        >Quitar sub-modelo</button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 pl-6">
                        {sg.variantes.map((v, vi) => (
                          <div key={v.id} className="flex flex-col text-xs" title={v.nombre_original}>
                            <Input
                              value={v.nombre_limpio}
                              onChange={(e) => setJerarquiaGrupos((gs) => gs.map((x, i) => i === gi ? { ...x, subgrupos: x.subgrupos.map((y, j) => j === sgi ? { ...y, variantes: y.variantes.map((z, k) => k === vi ? { ...z, nombre_limpio: e.target.value } : z) } : y) } : x))}
                              className="h-7 text-xs"
                              data-testid={`jer-var-${gi}-${sgi}-${vi}`}
                            />
                            <div className="text-[9px] text-muted-foreground/70 truncate mt-0.5">{v.nombre_original}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {(g.variantes_directas || []).length > 0 && (
                    <div className="ml-4 border-l-2 border-amber-300 pl-4 py-2 mb-2">
                      <div className="text-[11px] text-amber-800 font-semibold mb-2">Variantes directas del padre (sin sub-modelo)</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 pl-2">
                        {g.variantes_directas.map((v, vi) => (
                          <div key={v.id} className="flex flex-col text-xs" title={v.nombre_original}>
                            <Input
                              value={v.nombre_limpio}
                              onChange={(e) => setJerarquiaGrupos((gs) => gs.map((x, i) => i === gi ? { ...x, variantes_directas: x.variantes_directas.map((z, k) => k === vi ? { ...z, nombre_limpio: e.target.value } : z) } : x))}
                              className="h-7 text-xs"
                              data-testid={`jer-vd-${gi}-${vi}`}
                            />
                            <div className="text-[9px] text-muted-foreground/70 truncate mt-0.5">{v.nombre_original}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="flex justify-end pt-2 border-t border-border/40">
            <Button variant="outline" onClick={() => setJerarquiaOpen(false)}>Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Crear nuevo grupo con nombre + productos seleccionados (mar/2026) */}
      <Dialog open={nuevoGrupoOpen} onOpenChange={(v) => { if (!nuevoGrupoSaving) setNuevoGrupoOpen(v); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="crear-grupo-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Plus className="w-6 h-6 text-primary" /> Crear nuevo grupo
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              Un grupo agrupa productos que son "el mismo modelo con distintas variantes" (color, talla, etc). Le pones un nombre al grupo (ejemplo: "Cinta 4cm") y los productos seleccionados quedan como variantes del grupo.
            </p>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-y-auto">
            <div>
              <Label>Nombre del grupo *</Label>
              <Input
                value={nuevoGrupoNombre}
                onChange={(e) => setNuevoGrupoNombre(e.target.value)}
                placeholder="Ej: Cinta 4cm, Hilo carrete grande, Ropa niña talla 8"
                className="h-11 mt-1"
                data-testid="crear-grupo-nombre"
                autoFocus
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Este nombre aparecerá como cabecera del grupo en la vista de productos y en el catálogo público.
              </p>
            </div>

            <div>
              <Label>Productos en el grupo ({nuevoGrupoItems.size})</Label>
              <Input
                value={nuevoGrupoBuscar}
                onChange={(e) => setNuevoGrupoBuscar(e.target.value)}
                placeholder="Buscar productos por nombre, SKU, color…"
                className="h-10 mt-1"
                data-testid="crear-grupo-buscar"
              />
            </div>

            <div className="border border-border rounded-lg overflow-hidden">
              <div className="max-h-72 overflow-y-auto divide-y divide-border/50">
                {(() => {
                  const busca = nuevoGrupoBuscar.trim().toLowerCase();
                  // Filtramos: solo productos NO padres, NO archivados, y que si busca hay match
                  const cand = items
                    .filter((p) => !p.archivado && !p.es_padre)
                    .filter((p) => {
                      if (!busca) return nuevoGrupoItems.has(p.id); // sin búsqueda: solo los seleccionados
                      const txt = `${p.nombre || ""} ${p.sku || ""} ${p.color || ""} ${p.categoria || ""}`.toLowerCase();
                      return txt.includes(busca);
                    })
                    .slice(0, 100);
                  if (cand.length === 0) {
                    return (
                      <div className="p-6 text-center text-sm text-muted-foreground">
                        {busca ? `Sin resultados para "${busca}"` : "Selecciona productos escribiendo en el buscador arriba"}
                      </div>
                    );
                  }
                  return cand.map((p) => {
                    const checked = nuevoGrupoItems.has(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-muted/40 ${checked ? "bg-primary/5" : ""}`}
                        data-testid={`crear-grupo-item-${p.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            const next = new Set(nuevoGrupoItems);
                            if (e.target.checked) next.add(p.id);
                            else next.delete(p.id);
                            setNuevoGrupoItems(next);
                          }}
                          className="w-4 h-4"
                        />
                        <span className="flex-1 text-sm font-medium truncate">{p.nombre}</span>
                        <span className="text-[10px] text-muted-foreground truncate font-mono">{p.codigo || p.sku || "—"}</span>
                        {p.color && <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/70">{p.color}</span>}
                      </label>
                    );
                  });
                })()}
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground">
              💡 Los productos que agrupes quedarán como variantes del nombre del grupo. Podrás verlos con un click de expansión.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-border/40">
            <Button variant="outline" onClick={() => setNuevoGrupoOpen(false)} disabled={nuevoGrupoSaving}>
              Cancelar
            </Button>
            <Button
              disabled={nuevoGrupoSaving || !nuevoGrupoNombre.trim() || nuevoGrupoItems.size < 2}
              onClick={async () => {
                setNuevoGrupoSaving(true);
                try {
                  const { data } = await api.post("/productos/_/crear-grupo-nuevo", {
                    nombre_grupo: nuevoGrupoNombre.trim(),
                    variante_ids: Array.from(nuevoGrupoItems),
                  });
                  toast.success(`Grupo "${data.padre_nombre}" creado con ${data.variantes_agrupadas} variantes`);
                  setNuevoGrupoOpen(false);
                  setSelected(new Set()); // limpiar selección global
                  load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "No se pudo crear el grupo");
                } finally {
                  setNuevoGrupoSaving(false);
                }
              }}
              className="bg-primary hover:bg-primary/90"
              data-testid="crear-grupo-submit"
            >
              {nuevoGrupoSaving ? "Creando…" : `Crear grupo (${nuevoGrupoItems.size})`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * StockMinimoInline — input inline para editar el umbral de bajo stock por producto.
 * Se guarda en /productos/{pid}/stock-minimo al hacer blur o Enter.
 * Solo se renderiza si el usuario tiene el permiso; si no, muestra lectura.
 *
 * Modo asistido (opción C): si viene `sugerenciaIA.difiere = true`, se muestra
 * un badge ámbar "IA: N" al lado del input. Click en el badge → aplica el valor.
 * Hover → pide a Claude una explicación humana (lazy, en cache).
 */
function StockMinimoInline({ producto, canEdit, onUpdated }) {
  const [value, setValue] = useState(String(producto.stock_minimo ?? 0));
  const [saving, setSaving] = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setValue(String(producto.stock_minimo ?? 0));
  }, [producto.stock_minimo, focused]);

  const save = async (override) => {
    const n = Math.max(0, parseInt((override !== undefined ? override : value) || "0", 10) || 0);
    if (n === (producto.stock_minimo || 0)) return;
    setSaving(true);
    try {
      await api.patch(`/productos/${producto.id}/stock-minimo`, { stock_minimo: n });
      toast.success(`Mínimo de "${producto.nombre.slice(0, 30)}${producto.nombre.length > 30 ? '…' : ''}" ajustado a ${n}`);
      onUpdated?.(producto.id, n);
      if (override !== undefined) setValue(String(n));
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo guardar";
      toast.error(msg);
      setValue(String(producto.stock_minimo ?? 0));
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <span className="text-[10px] text-muted-foreground/70 tabular-nums">
        mín: {producto.stock_minimo || 0}
      </span>
    );
  }

  const esManual = !!producto.stock_minimo_manual;
  const info = producto.stock_minimo_auto_info;
  const volverAuto = async () => {
    if (!window.confirm(`Volver a mínimo automático para "${producto.nombre.slice(0, 30)}"? El sistema lo calculará según el consumo.`)) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/productos/${producto.id}/stock-minimo/automatico`);
      toast.success(`Mínimo automático: ${data.stock_minimo}`);
      onUpdated?.(producto.id, data.stock_minimo, { stock_minimo_manual: false, stock_minimo_auto_info: data.info });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cambiar"); } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-1 justify-end mt-0.5 flex-wrap">
      <span className="text-[10px] text-muted-foreground/70">mín:</span>
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); save(); }}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        disabled={saving}
        className="w-14 h-6 text-[11px] px-1.5 rounded border border-border/60 bg-card text-right tabular-nums hover:border-primary/40 focus:border-primary focus:outline-none disabled:opacity-50"
        title={esManual ? "Mínimo fijado a mano. Si lo editas sigue manual." : `Mínimo automático según consumo${info ? ` (${info.ritmo_dia} und/día × ${info.lead_time_dias} días + ${info.colchon_pct}%, piso ${info.piso})` : ""}. Si lo editas pasa a manual.`}
        data-testid={`stock-min-inline-${producto.id}`}
      />
      {esManual ? (
        <button type="button" onClick={volverAuto} disabled={saving} className="text-[9px] h-5 px-1.5 rounded-full border border-border text-muted-foreground hover:bg-accent" title="Fijado a mano · click para volver a automático" data-testid={`stock-min-manual-${producto.id}`}>manual</button>
      ) : (
        <span className="text-[9px] h-5 px-1.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 inline-flex items-center" title={info ? `Calculado el ${(info.at || "").slice(0, 10)}: ${info.ritmo_dia} und/día` : "Automático según consumo"} data-testid={`stock-min-auto-${producto.id}`}>auto</span>
      )}
    </div>
  );
}

